import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import './ComplianceStructureBuilder.css';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
import { notify } from '@/core/notifications/notify';
import { NewBadge } from '@/contexts/NewFeaturesContext';
import ComplianceStructureBuilderRenderer from './ComplianceStructureBuilderRenderer';
import { useModal, ConfirmModal, FrameworkAssignmentWizard } from '@components/modal';
import PageInfoButton from '@/components/PageInfoButton';
import { VerificationPanel } from '@/domains/compliance/lifecycle/VerificationPanel';

// ─────────────────────────────────────────────────────────────────────────
// NodeQuestionsPanel — builder UI for the per-requirement question banks
// added in migration 048. Lives in two modes:
//   guidance  → shown to compliance_user when filling the page
//   evidence  → shown to internal_auditor / external_auditor when reviewing
// Both flow through the framework assignment lifecycle automatically because
// they're attached to the template_node, not duplicated per company.
// ─────────────────────────────────────────────────────────────────────────
interface NodeQuestion {
  id: number;
  template_node_id: number;
  template_id: number;
  mode: 'guidance' | 'evidence';
  question_text: string;
  hint: string | null;
  suggested_evidence_type: string | null;
  is_required: boolean;
  order_index: number;
}

const NodeQuestionsPanel: React.FC<{ nodeId: number }> = ({ nodeId }) => {
  const [questions, setQuestions] = React.useState<NodeQuestion[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [drafts, setDrafts] = React.useState<{ guidance: string; evidence: string }>({ guidance: '', evidence: '' });

  const authHeaders = (): HeadersInit => ({
    Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
    'Content-Type': 'application/json',
    'X-CSRF-Token': getCsrfToken(),
  });

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/compliance/definition/template-node-questions/by-node/${nodeId}`, {
        headers: authHeaders(),
      });
      const json = await res.json();
      if (json?.success) setQuestions(json.data || []);
    } finally {
      setLoading(false);
    }
  }, [nodeId]);

  React.useEffect(() => { void load(); }, [load]);

  const addQuestion = async (mode: 'guidance' | 'evidence') => {
    const text = drafts[mode].trim();
    if (!text) return;
    try {
      const res = await fetch('/api/compliance/definition/template-node-questions', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ template_node_id: nodeId, mode, question_text: text }),
      });
      const json = await res.json();
      if (json?.success) {
        setDrafts((d) => ({ ...d, [mode]: '' }));
        await load();
      } else {
        notify.error(json?.error || 'Failed to add question');
      }
    } catch (e: any) {
      notify.error(e?.message || 'Failed to add question');
    }
  };

  const removeQuestion = async (id: number) => {
    try {
      await fetch(`/api/compliance/definition/template-node-questions/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      await load();
    } catch (e: any) {
      notify.error(e?.message || 'Failed to delete question');
    }
  };

  const guidance = questions.filter((q) => q.mode === 'guidance');
  const evidence = questions.filter((q) => q.mode === 'evidence');

  const renderSection = (
    mode: 'guidance' | 'evidence',
    list: NodeQuestion[],
    title: string,
    subtitle: string,
    accent: string,
    placeholder: string,
  ) => (
    <div className="mt-3 border border-slate-200/60 rounded-xl p-3 bg-white/60">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-700">
          {title}
        </div>
        <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded ${accent}`}>{list.length}</span>
      </div>
      <div className="text-[10px] text-slate-500 mb-2">{subtitle}</div>
      {list.length > 0 && (
        <ul className="space-y-1.5 mb-2">
          {list.map((q) => (
            <li key={q.id} className="flex items-start gap-2 text-[11px]">
              <span className="flex-1 text-slate-700">{q.question_text}</span>
              <button
                type="button"
                onClick={() => removeQuestion(q.id)}
                className="text-[10px] text-rose-500 hover:text-rose-700 font-bold"
                title="Delete this question"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-1.5">
        <input
          type="text"
          value={drafts[mode]}
          onChange={(e) => setDrafts((d) => ({ ...d, [mode]: e.target.value }))}
          placeholder={placeholder}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void addQuestion(mode);
            }
          }}
          className="flex-1 text-[11px] px-2 py-1 border border-slate-200 rounded focus:ring-2 ring-indigo-100 focus:border-indigo-400 outline-none"
        />
        <button
          type="button"
          onClick={() => void addQuestion(mode)}
          disabled={!drafts[mode].trim()}
          className="px-2 py-1 text-[10px] font-bold uppercase rounded border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-40"
        >
          Add
        </button>
      </div>
    </div>
  );

  return (
    <div className="mt-4">
      <div className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Questions on this requirement</div>
      <div className="text-[10px] text-slate-500 mb-2">Authored on the template — flow through to every assigned company automatically.</div>
      {loading && <div className="text-[10px] text-slate-400 italic">Loading…</div>}
      {renderSection(
        'guidance',
        guidance,
        'Guidance — for the compliance officer',
        'Shown to the user filling the page so they know what is being asked.',
        'bg-emerald-100 text-emerald-700',
        'e.g. What controls do you have in place for X?',
      )}
      {renderSection(
        'evidence',
        evidence,
        'Evidence — for the auditor',
        'Shown to internal / external auditors when verifying the submission.',
        'bg-amber-100 text-amber-800',
        'e.g. Verify a sample of access reviews from the last 90 days.',
      )}
    </div>
  );
};

// Drag types
type DragType = 'scope' | 'section' | 'requirement' | 'sub_requirement' | 'field';

// Field types
type FieldType = 'text' | 'number' | 'date' | 'boolean' | 'document' | 'calculated' | 'select' | 'multiselect' | 'textarea';

// Types
interface Template {
  id: number;
  name: string;
  description?: string;
  status: 'draft' | 'published' | 'archived';
  version: number;
  createdAt: string;
  updatedAt: string;
  generation_source?: string;
}

interface Node {
  id: string;
  type: 'scope' | 'section' | 'requirement' | 'sub_requirement';
  name: string;
  description?: string | undefined;
  children?: Node[];
  fields?: TemplateField[];
  generates_compliance_page?: boolean;
  evidence_required?: boolean;
  evidence_type?: 'document' | 'number' | 'text' | 'mixed';
  assessor_only_notes?: string;
  clause_ref?: string | null;
  source_ref?: string | null;
}

// Template Node interface matching the API
interface TemplateNode {
  id: number;
  template_id: number;
  parent_id?: number;
  name: string;
  description?: string;
  node_type: 'scope' | 'section' | 'requirement' | 'sub_requirement';
  node_order: number;
  is_active: boolean;
  generates_compliance_page?: boolean;
  evidence_required?: boolean;
  evidence_type?: 'document' | 'number' | 'text' | 'mixed';
  assessor_only_notes?: string;
  clause_ref?: string | null;
  source_ref?: string | null;
  children?: TemplateNode[];
}

// Template Field interface matching the API
interface TemplateField {
  id: number;
  node_id: number;
  field_name: string;
  field_type: string;
  description?: string;
  field_options?: any;
  field_order: number;
  is_required: boolean;
  is_active: boolean;
  validation_rules?: any;
  default_value?: any;
  is_evidence?: boolean;
  allow_multiple?: boolean;
  created_at: string;
  updated_at: string;
}

const normalizeFieldOptions = (fieldOptions: any): string[] => {
  if (fieldOptions == null) return [];

  let parsed = fieldOptions;

  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      parsed = parsed
        .split('\n')
        .map((opt: string) => opt.trim())
        .filter(Boolean);
    }
  }

  if (Array.isArray(parsed)) {
    return parsed
      .map((opt: any) => (typeof opt === 'string' ? opt : opt?.label ?? opt?.value ?? ''))
      .map((opt: string) => opt.trim())
      .filter(Boolean);
  }

  if (Array.isArray(parsed?.options)) {
    return parsed.options
      .map((opt: any) => (typeof opt === 'string' ? opt : opt?.label ?? opt?.value ?? ''))
      .map((opt: string) => opt.trim())
      .filter(Boolean);
  }

  return [];
};

const parseOptionsFromEditorText = (value: string): string[] =>
  value
    .split('\n')
    .map((opt) => opt.trim())
    .filter(Boolean);

const isSelectLikeFieldType = (fieldType?: string): boolean =>
  fieldType === 'select' || fieldType === 'multiselect';

// Helper function to build node tree from flat list
const buildNodeTree = (nodes: any): TemplateNode[] => {
  if (!Array.isArray(nodes)) {
    console.error('buildNodeTree: nodes is not an array:', nodes);
    return [];
  }

  const nodeMap = new Map<number, TemplateNode>();
  const rootNodes: TemplateNode[] = [];

  nodes.forEach(node => {
    nodeMap.set(node.id, { ...node, children: [] });
  });

  // Second pass: build hierarchy
  nodes.forEach(node => {
    const nodeWithChildren = nodeMap.get(node.id)!;
    if (node.parent_id) {
      const parent = nodeMap.get(node.parent_id);
      if (parent) {
        parent.children!.push(nodeWithChildren);
      } else {
        // Parent not found - treat as root node instead of error
        rootNodes.push(nodeWithChildren);
      }
    } else {
      rootNodes.push(nodeWithChildren);
    }
  });

  // Sort children by node_order
  const sortChildren = (node: TemplateNode) => {
    if (node.children) {
      node.children.sort((a, b) => (a.node_order || 0) - (b.node_order || 0));
      node.children.forEach(sortChildren);
    }
  };

  rootNodes.forEach(sortChildren);

  return rootNodes;
};

// Helper function to recursively update a node in the tree
const updateNodeInTree = (nodes: Node[], updatedNode: Node): Node[] => {
  return nodes.map(node => {
    if (node.id === updatedNode.id) {
      return updatedNode;
    }
    if (node.children) {
      return {
        ...node,
        children: updateNodeInTree(node.children, updatedNode)
      };
    }
    return node;
  });
};

// Helper function to find a node by id in the tree
const findNodeById = (nodes: Node[], id: string): Node | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children) {
      const found = findNodeById(node.children, id);
      if (found) return found;
    }
  }
  return null;
};

// Helper function to find the parent of a node in the tree
const findParent = (nodes: Node[], targetNode: Node, parent: Node | null = null): Node | null => {
  for (const node of nodes) {
    if (node.id === targetNode.id) {
      return parent;
    }
    if (node.children) {
      const found = findParent(node.children, targetNode, node);
      if (found !== null) return found;
    }
  }
  return null;
};

// Helper function to find the section ancestor of a node
const findSectionAncestor = (nodes: Node[], node: Node): Node | null => {
  const parent = findParent(nodes, node);
  if (!parent) return null;
  if (parent.type === 'section') return parent;
  return findSectionAncestor(nodes, parent);
};

// Helper function to check if any other node in the same section has compliance page
const hasConflictingCompliancePage = (nodes: Node[], selectedNode: Node): boolean => {
  if (selectedNode.type === 'scope' || selectedNode.type === 'section') {
    return false; // No checkboxes for these
  }

  const section = findSectionAncestor(nodes, selectedNode);
  if (!section) return false;

  // Get all descendants of the section that can generate pages
  const getDescendants = (node: Node): Node[] => {
    const descendants: Node[] = [];
    const collect = (n: Node) => {
      if (n.children) {
        n.children.forEach(child => {
          if (child.type === 'requirement' || child.type === 'sub_requirement') {
            descendants.push(child);
          }
          collect(child);
        });
      }
    };
    collect(node);
    return descendants;
  };

  const sectionDescendants = getDescendants(section);
  const pageGenerators = sectionDescendants.filter(d => d.generates_compliance_page);

  // If any other node in the section has compliance page, conflict
  return pageGenerators.some(pg => pg.id !== selectedNode.id);
};

// Draggable Component for Sidebar
const DraggableComponent: React.FC<{
  type: DragType;
  name: string;
  description: string;
  onDragStart: (type: DragType) => void;
  isDraft: boolean;
}> = ({ type, name, description, onDragStart, isDraft }) => {
  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('text/plain', type);
    onDragStart(type);
  };

  const getClassName = () => {
    const disabledClasses = "opacity-50 cursor-not-allowed bg-slate-50";

    if (type === 'scope') {
      return `p-4 border-2 border-slate-900 rounded-2xl bg-white ${isDraft ? 'cursor-grab hover:shadow-lg active:scale-95' : disabledClasses}`;
    } else if (type === 'section') {
      return `p-4 border-2 border-slate-400 rounded-2xl bg-white ${isDraft ? 'cursor-grab hover:border-slate-600 active:scale-95' : disabledClasses}`;
    } else if (type === 'requirement') {
      return `p-4 border border-slate-200 rounded-2xl bg-white ${isDraft ? 'cursor-grab hover:border-emerald-500 active:scale-95' : disabledClasses}`;
    } else if (type === 'sub_requirement') {
      return `p-3 border border-dashed border-slate-300 rounded-xl bg-white/50 ${isDraft ? 'cursor-grab hover:border-amber-500 active:scale-95' : disabledClasses}`;
    } else {
      return `p-3 border border-dashed border-indigo-300 rounded-xl bg-indigo-50/50 ${isDraft ? 'cursor-grab hover:border-indigo-500 active:scale-95' : disabledClasses}`;
    }
  };

  return (
    <div
      draggable={isDraft}
      onDragStart={isDraft ? handleDragStart : undefined}
      className={getClassName()}
    >
      <p className="text-xs font-bold">{name}</p>
      {description && <p className="text-[9px] text-slate-400 mt-1 uppercase font-bold">{description}</p>}
      {type === 'sub_requirement' && <p className="text-[11px] font-bold text-slate-500 italic">Sub-Requirement</p>}
      {type === 'field' && <p className="text-[11px] font-bold text-indigo-500 italic">Field Component</p>}
    </div>
  );
};

// Draggable Field Component for Sidebar
const DraggableFieldComponent: React.FC<{
  fieldType: FieldType;
  name: string;
  description: string;
  onDragStart: (fieldType: FieldType) => void;
  isDraft: boolean;
}> = ({ fieldType, name, description, onDragStart, isDraft }) => {
  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('text/plain', `field:${fieldType}`);
    onDragStart(fieldType);
  };

  const getIcon = () => {
    switch (fieldType) {
      case 'text': return 'T';
      case 'number': return '#';
      case 'date': return '📅';
      case 'boolean': return '✓';
      case 'document': return '📄';
      case 'calculated': return '∑';
      case 'select': return '▼';
      case 'multiselect': return '▤';
      case 'textarea': return '¶';
      default: return '?';
    }
  };

  return (
    <div
      draggable={isDraft}
      onDragStart={isDraft ? handleDragStart : undefined}
      className={`p-3 border border-dashed border-indigo-300 rounded-xl bg-indigo-50 transition-all shadow-sm ${
        isDraft
          ? 'cursor-grab hover:border-indigo-500/50 active:scale-95'
          : 'opacity-50 cursor-not-allowed bg-slate-50'
      }`}
    >
      <div className="flex items-center gap-2">
        <span className="w-6 h-6 bg-indigo-200 rounded-lg flex items-center justify-center text-xs font-bold text-indigo-700 shadow-sm">
          {getIcon()}
        </span>
        <div className="flex-1">
          <p className="text-xs font-bold text-slate-700">{name}</p>
          {description && <p className="text-[9px] text-slate-400 mt-0.5 uppercase font-bold">{description}</p>}
        </div>
      </div>
    </div>
  );
};

const ComplianceStructureBuilder: React.FC = () => {
  const { openModal } = useModal();
  const [searchParams, setSearchParams] = useSearchParams();

  // Phase 3: framework + version context from URL (Lifecycle Author flow).
  // See docs/COMPLIANCE_LIFECYCLE_PLAN.md §7 Phase 3.
  const ctxFrameworkId = searchParams.get('frameworkId') ? Number(searchParams.get('frameworkId')) : null;
  const ctxVersionId = searchParams.get('versionId') ? Number(searchParams.get('versionId')) : null;
  const ctxPreviousVersionId = searchParams.get('previousVersionId') ? Number(searchParams.get('previousVersionId')) : null;

  // Phase 3: verification engine UI state
  const [verifyResult, setVerifyResult] = useState<any>(null);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [showVerifyPanel, setShowVerifyPanel] = useState(false);
  const [publishActivateLoading, setPublishActivateLoading] = useState(false);
  const verifyDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // State
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [nodes, setNodes] = useState<Node[]>([]);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [loading, setLoading] = useState(false);
  const [draggedType, setDraggedType] = useState<DragType | null>(null);
  const [draggedFieldType, setDraggedFieldType] = useState<FieldType | null>(null);
  const [isCreatingDomain, setIsCreatingDomain] = useState(false);
  const [nodeFields, setNodeFields] = useState<Map<string, TemplateField[]>>(new Map());
  const [selectedField, setSelectedField] = useState<TemplateField | null>(null);
  const [selectedFieldOptionsText, setSelectedFieldOptionsText] = useState('');
  const [draggedField, setDraggedField] = useState<TemplateField | null>(null);
  const [dragOverFieldId, setDragOverFieldId] = useState<number | null>(null);
  const [dragOverPosition, setDragOverPosition] = useState<'before' | 'after' | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const originalFieldRef = useRef<TemplateField | null>(null);
  const originalNodeRef = useRef<Node | null>(null);
  const [isCreatingSubReq, setIsCreatingSubReq] = useState(false);

  // AI assist state. `aiBusyKey` is a scoped key like "node:366:title" so
  // only the busy toolbar shows a spinner. `aiUndoMap` holds the pre-AI
  // value for that same key so the user can revert one click later.
  const [aiBusyKey, setAiBusyKey] = useState<string | null>(null);
  const [aiUndoMap, setAiUndoMap] = useState<Record<string, string>>({});

  // Builder edit mode: the classic drag-drop tree, or the structured
  // list editor (same flavour as the Describe Standard preview window
  // — flat recursive list with inline title / description edit per row).
  // Toggled from the header. Operates on the same `nodes` state, so
  // edits made in either mode persist to the same template.
  const [builderMode, setBuilderMode] = useState<'visual' | 'structured'>('visual');
  // Row-level edit state for the structured view. Only one row edits at a
  // time; Save fires updateNode, Cancel discards.
  const [structEditingId, setStructEditingId] = useState<string | null>(null);
  const [structEditForm, setStructEditForm] = useState<{ name: string; description: string }>({ name: '', description: '' });

  // Auto-save state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastAutoSaveTime, setLastAutoSaveTime] = useState<Date | null>(null);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Map mock IDs to real IDs
  const mockToReal = useRef(new Map<string, number>());

  // Modal states
  const [showHelpPanel, setShowHelpPanel] = useState(false);
  const [showCreateTemplateForm, setShowCreateTemplateForm] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');

  // Recent templates for landing page
  const [recentTemplates, setRecentTemplates] = useState<Template[]>([]);

  // Drag handling
  const handleDragStart = (type: DragType) => {
    console.log('Drag start:', type);
    setDraggedType(type);
    setDraggedFieldType(null); // Reset field drag state
  };

  const handleFieldDragStart = (fieldType: FieldType) => {
    console.log('Field drag start:', fieldType);
    setDraggedFieldType(fieldType);
    setDraggedType(null); // Reset component drag state
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape: Close modals
      if (e.key === 'Escape') {
        setShowHelpPanel(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showHelpPanel]);

  // Drop handlers
  const handleCanvasDrop = (type: DragType) => {
    if (type === 'scope' && !isCreatingDomain) {
      createDomainBundle();
    }
  };

  const handleZoneDrop = (zoneType: string, parentId: string) => (type: DragType) => {
    if (zoneType === 'section' && type === 'section') {
      console.log('Calling addSection');
      addSection(parentId); // Sections are always under scopes, so use original ID
    } else if (zoneType === 'requirement' && type === 'requirement') {
      console.log('Calling addRequirement');
      addRequirement(parentId); // Requirements are always under sections, so use original ID
    } else if (zoneType === 'sub_requirement' && type === 'sub_requirement') {
      console.log('Calling addSubRequirement with parentId:', parentId);
      // For sub-requirements, check if we have a real ID mapping, otherwise use the original ID
      const realId = mockToReal.current.get(parentId);
      const targetId = realId ? realId.toString() : parentId;
      console.log('Using targetId for sub-requirement:', targetId);
      addSubRequirement(targetId);
    } else {
      console.log('No matching condition for zoneType:', zoneType, 'type:', type);
    }
  };

  const handleFieldDrop = (nodeId: string, fieldType: FieldType) => {
    let realNodeId: string = nodeId;
    const mappedId = mockToReal.current.get(nodeId);
    if (mappedId) {
      realNodeId = mappedId.toString();
    }
    addFieldToNode(realNodeId, fieldType);
  };

  // Create domain bundle
  const createDomainBundle = async () => {
    if (isCreatingDomain) return;
    setIsCreatingDomain(true);

    // Create mock data for immediate UI feedback - use unique ID
    const mockId = `scope-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newNode: Node = {
      id: mockId,
      type: 'scope',
      name: 'Domain / Scope Title',
      description: 'Objective Section Name',
      children: []
    };

    // Immediate UI update
    setNodes(prev => [...prev, newNode]);

    try {
      if (!selectedTemplate) {
        // Create template in background
        const template = await createTemplate('New Compliance Template', 'Created from Compliance Hub');
        if (template) {
          setSelectedTemplate(template);
          // Update the mock node with real ID
          const scopeNode = await createNode(template.id, 'Domain / Scope Title', 'scope', undefined, 0);
          if (scopeNode) {
            mockToReal.current.set(mockId, scopeNode.id);
            setNodes(prev => prev.map(node =>
              node.id === mockId ? { ...node, id: scopeNode.id.toString() } : node
            ));
          }
        }
      } else {
        // Calculate next node order for scopes
        const existingScopes = nodes.filter(n => n.type === 'scope');
        const nextOrder = existingScopes.length;

        // Create node in background
        const scopeNode = await createNode(selectedTemplate.id, 'Domain / Scope Title', 'scope', undefined, nextOrder);
        if (scopeNode) {
          mockToReal.current.set(mockId, scopeNode.id);
          setNodes(prev => prev.map(node =>
            node.id === mockId ? { ...node, id: scopeNode.id.toString() } : node
          ));
        }
      }
    } catch (error) {
      console.error('Error creating domain:', error);
      // Remove the mock node if creation failed
      setNodes(prev => prev.filter(node => node.id !== mockId));
    } finally {
      setIsCreatingDomain(false);
    }
  };

  // Add section to a scope
  const addSection = async (scopeId: string) => {
    if (!selectedTemplate) return;

    // Create mock data for immediate UI feedback
    const mockId = `section-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newSection: Node = {
      id: mockId,
      type: 'section',
      name: 'Section',
      description: '',
      children: []
    };

    // Immediate UI update - use functional update to avoid stale state
    setNodes(prev => {
      return prev.map(node => {
        if (node.id === scopeId) {
          return { ...node, children: [...(node.children || []), newSection] };
        }
        return node;
      });
    });

    // Calculate next order based on existing sections
    const scopeNode = nodes.find(n => n.id === scopeId);
    const existingSections = scopeNode?.children?.filter(c => c.type === 'section') || [];
    const nextOrder = existingSections.length;

    // Get the real parent ID
    let realParentId: number | undefined;
    const mappedId = mockToReal.current.get(scopeId);
    if (mappedId) {
      realParentId = mappedId;
    } else {
      const parsed = parseInt(scopeId);
      if (!isNaN(parsed)) {
        realParentId = parsed;
      }
    }

    try {
      // Create node in background
      const sectionNode = await createNode(selectedTemplate.id, 'Section', 'section', realParentId, nextOrder);

      if (sectionNode) {
        mockToReal.current.set(mockId, sectionNode.id);

        // Update the node with the real ID
        setNodes(prev => {
          return prev.map(node => {
            if (!node.children) return node;

            return {
              ...node,
              children: node.children.map(child =>
                child.id === mockId ? { ...child, id: sectionNode.id.toString() } : child
              )
            };
          });
        });
        // Trigger auto-save
        triggerAutoSave();
      }
    } catch (error) {
      console.error('Error creating section:', error);
      // Remove the mock node if creation failed
      setNodes(prev => {
        return prev.map(node => {
          if (!node.children) return node;

          return {
            ...node,
            children: node.children.filter(child => child.id !== mockId)
          };
        });
      });
    }
  };

  // Helper function to find a node by ID recursively
  const findNodeById = (nodes: Node[], targetId: string): Node | null => {
    for (const node of nodes) {
      if (node.id === targetId) {
        return node;
      }
      if (node.children) {
        const found = findNodeById(node.children, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  // Add requirement to a section
  const addRequirement = async (sectionId: string) => {
    if (!selectedTemplate) return;

    // Find the section node recursively (it might be nested under a scope)
    const sectionNode = findNodeById(nodes, sectionId);
    const existingReqs = sectionNode?.children?.filter(c => c.type === 'requirement') || [];
    const nextOrder = existingReqs.length;

    // Create mock data for immediate UI feedback.
    // Phase 3 defaults: new requirement is page-generating + document evidence type
    // so it's immediately usable. These match what createNode() sends to the server.
    const mockId = `req-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newReq: Node = {
      id: mockId,
      type: 'requirement',
      name: 'Title',
      description: 'Description...',
      children: [],
      generates_compliance_page: true,
      evidence_required: true,
      evidence_type: 'document',
      clause_ref: null,
      source_ref: null,
    };

    // Helper function to update nodes recursively
    const updateNodeChildren = (nodes: Node[], targetId: string, newChild: Node): Node[] => {
      return nodes.map(node => {
        if (node.id === targetId) {
          return { ...node, children: [...(node.children || []), newChild] };
        }
        if (node.children) {
          return { ...node, children: updateNodeChildren(node.children, targetId, newChild) };
        }
        return node;
      });
    };

    // Immediate UI update - use functional update to avoid stale state
    setNodes(prev => updateNodeChildren(prev, sectionId, newReq));

    // Get the real parent ID
    let realParentId: number | undefined;
    const mappedId = mockToReal.current.get(sectionId);
    if (mappedId) {
      realParentId = mappedId;
    } else {
      const parsed = parseInt(sectionId);
      if (!isNaN(parsed)) {
        realParentId = parsed;
      }
    }

    try {
      // Create node in background
      const reqNode = await createNode(selectedTemplate.id, 'Title', 'requirement', realParentId, nextOrder);

      if (reqNode) {
        mockToReal.current.set(mockId, reqNode.id);

        // Update the node with the real ID (recursive update)
        setNodes(prev => {
          const updateMockToReal = (nodes: Node[]): Node[] => {
            return nodes.map(node => {
              // Check if this node itself has the mock ID
              if (node.id === mockId) {
                return { ...node, id: reqNode.id.toString() };
              }

              if (node.children) {
                return {
                  ...node,
                  children: node.children.map(child => {
                    if (child.id === mockId) {
                      return { ...child, id: reqNode.id.toString() };
                    }
                    if (child.children) {
                      return { ...child, children: updateMockToReal(child.children) };
                    }
                    return child;
                  })
                };
              }
              return node;
            });
          };
          return updateMockToReal(prev);
        });
        // Trigger auto-save
        triggerAutoSave();
      }
    } catch (error) {
      console.error('Error creating requirement:', error);
      // Remove the mock node if creation failed (recursive removal)
      setNodes(prev => {
        const removeMockNode = (nodes: Node[]): Node[] => {
          return nodes.map(node => {
            if (node.children) {
              return {
                ...node,
                children: node.children.filter(child => child.id !== mockId)
                  .map(child => child.children ? { ...child, children: removeMockNode(child.children) } : child)
              };
            }
            return node;
          });
        };
        return removeMockNode(prev);
      });
    }
  };

  // Add sub-requirement to a requirement
  const addSubRequirement = async (reqId: string) => {
    if (!selectedTemplate) return;

    // Prevent multiple simultaneous additions
    if (isCreatingSubReq) return;
    setIsCreatingSubReq(true);

    // Find the requirement node recursively
    const reqNode = findNodeById(nodes, reqId);
    const existingSubs = reqNode?.children?.filter(c => c.type === 'sub_requirement') || [];
    const nextOrder = existingSubs.length;

    // Create mock data for immediate UI feedback.
    // Sub-requirements default to GCP=false to avoid the one-page-per-branch
    // conflict when the parent requirement is already page-generating.
    // Matches the server-side createNode() default for sub_requirements.
    const mockId = `sub-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newSub: Node = {
      id: mockId,
      type: 'sub_requirement',
      name: 'Sub-task...',
      children: [],
      generates_compliance_page: false,
      evidence_required: false,
      evidence_type: undefined,
      clause_ref: null,
      source_ref: null,
    };

    // Helper function to update nodes recursively
    const updateNodeChildren = (nodes: Node[], targetId: string, newChild: Node): Node[] => {
      return nodes.map(node => {
        if (node.id === targetId) {
          return { ...node, children: [...(node.children || []), newChild] };
        }
        if (node.children) {
          return { ...node, children: updateNodeChildren(node.children, targetId, newChild) };
        }
        return node;
      });
    };

    // Immediate UI update - use functional update to avoid stale state
    setNodes(prev => updateNodeChildren(prev, reqId, newSub));

    // Get the real parent ID
    let realParentId: number | undefined;
    const mappedId = mockToReal.current.get(reqId);
    if (mappedId) {
      realParentId = mappedId;
    } else {
      const parsed = parseInt(reqId);
      if (!isNaN(parsed)) {
        realParentId = parsed;
      }
    }

    try {
      // Create node in background
      const subNode = await createNode(selectedTemplate.id, 'Sub-task...', 'sub_requirement', realParentId, nextOrder);

      if (subNode) {
        mockToReal.current.set(mockId, subNode.id);

        // Update the node with the real ID (recursive update)
        setNodes(prev => {
          const updateMockToReal = (nodes: Node[]): Node[] => {
            return nodes.map(node => {
              // Check if this node itself has the mock ID
              if (node.id === mockId) {
                return { ...node, id: subNode.id.toString() };
              }

              if (node.children) {
                return {
                  ...node,
                  children: node.children.map(child => {
                    if (child.id === mockId) {
                      return { ...child, id: subNode.id.toString() };
                    }
                    if (child.children) {
                      return { ...child, children: updateMockToReal(child.children) };
                    }
                    return child;
                  })
                };
              }
              return node;
            });
          };
          return updateMockToReal(prev);
        });
        // Trigger auto-save
        triggerAutoSave();
      }
    } catch (error) {
      console.error('Error creating sub-requirement:', error);
      // Remove the mock node if creation failed (recursive removal)
      setNodes(prev => {
        const removeMockNode = (nodes: Node[]): Node[] => {
          return nodes.map(node => {
            if (node.children) {
              return {
                ...node,
                children: node.children.filter(child => child.id !== mockId)
                  .map(child => child.children ? { ...child, children: removeMockNode(child.children) } : child)
              };
            }
            return node;
          });
        };
        return removeMockNode(prev);
      });
    } finally {
      setIsCreatingSubReq(false);
    }
  };

  // Add field to a node
  const addFieldToNode = async (nodeId: string, fieldType: FieldType) => {
    if (!selectedTemplate) return;

    let realNodeId: number | undefined;
    const mappedId = mockToReal.current.get(nodeId);
    if (mappedId) {
      realNodeId = mappedId;
    } else {
      const parsed = parseInt(nodeId);
      if (!isNaN(parsed)) {
        realNodeId = parsed;
      }
    }

    if (!realNodeId) {
      console.error('Cannot add field: node ID not found');
      return;
    }

    // Generate a placeholder name from the type. Users rename this via the
    // Properties panel; the "Field Name" input there is the question text the
    // compliance user will see, so we start with a human-readable prompt
    // instead of the bare type name ("Boolean", "Document", etc.).
    const fieldName = `New ${fieldType === 'boolean' ? 'yes/no' : fieldType} question`;
    const fieldLabel = fieldName;

    try {
      const response = await fetch(`/api/compliance/definition/templates/nodes/${realNodeId}/fields`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({
          label: fieldLabel,
          field_name: fieldName,
          field_type: fieldType,
          description: `${fieldLabel} field`,
          field_options: isSelectLikeFieldType(fieldType) ? [] : null,
          field_order: 0,
          is_required: false,
          is_active: true,
          validation_rules: null,
          default_value: null,
          is_evidence: false,
          allow_multiple: fieldType === 'multiselect'
        })
      });

      if (response.ok) {
        notify.success('Field added successfully');
        loadNodeFields(realNodeId);
        // Trigger auto-save
        triggerAutoSave();
      } else {
        const error = await response.json();
        notify.error(error.error || 'Failed to add field');
      }
    } catch (error) {
      console.error('Error adding field:', error);
      notify.error('Failed to add field');
    }
  };

  // Load fields for a node
  const loadNodeFields = async (nodeId: number) => {
    try {
      const response = await fetch(`/api/compliance/definition/templates/nodes/${nodeId}/fields`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      const fields = (data.data || []).map((field: TemplateField) => ({
        ...field,
        field_options: normalizeFieldOptions(field.field_options)
      }));

      // Update the nodeFields map
      setNodeFields(prev => {
        const newMap = new Map(prev);
        newMap.set(nodeId.toString(), fields);
        return newMap;
      });

      // Also update the nodes state to include fields
      setNodes(prev => {
        const updateNodeFields = (nodes: Node[]): Node[] => {
          return nodes.map(node => {
            if (node.id === nodeId.toString()) {
              return { ...node, fields };
            }
            if (node.children) {
              return { ...node, children: updateNodeFields(node.children) };
            }
            return node;
          });
        };
        return updateNodeFields(prev);
      });
    } catch (error) {
      console.error('Error loading node fields:', error);
      notify.error('Failed to load node fields');
    }
  };

  // Delete field
  const deleteField = async (nodeId: number, fieldId: number) => {
    try {
      const response = await fetch(`/api/compliance/definition/templates/fields/${fieldId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'X-CSRF-Token': getCsrfToken() }
      });

      if (response.ok) {
        notify.success('Field deleted successfully');
        loadNodeFields(nodeId);
        // Trigger auto-save
        triggerAutoSave();
      } else {
        const error = await response.json();
        notify.error(error.error || 'Failed to delete field');
      }
    } catch (error) {
      console.error('Error deleting field:', error);
      notify.error('Failed to delete field');
    }
  };

  // Update node
  const updateNode = async (node: Node, opts: { silent?: boolean } = {}) => {
    // Get the real node ID
    let realNodeId: number;
    const mappedId = mockToReal.current.get(node.id);
    if (mappedId) {
      realNodeId = mappedId;
    } else {
      const parsed = parseInt(node.id);
      if (isNaN(parsed)) {
        console.error('Invalid node ID for update:', node.id);
        notify.error('Invalid node ID');
        return;
      }
      realNodeId = parsed;
    }

    try {
      const response = await fetch(`/api/compliance/definition/templates/nodes/${realNodeId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({
          name: node.name,
          description: node.description,
          generates_compliance_page: node.generates_compliance_page,
          evidence_required: node.evidence_required,
          evidence_type: node.evidence_type,
          assessor_only_notes: node.assessor_only_notes,
          clause_ref: node.clause_ref ?? null,
          source_ref: node.source_ref ?? null
        })
      });

      if (response.ok) {
        if (!opts.silent) notify.success('Node updated successfully');
        // Update local state (recursive update for tree structure)
        setNodes(prev => updateNodeInTree(prev, node));
        // Trigger auto-save
        triggerAutoSave();
      } else {
        const error = await response.json();
        notify.error(error.error || 'Failed to update node');
      }
    } catch (error) {
      console.error('Error updating node:', error);
      notify.error('Failed to update node');
    }
  };

  // Generic AI assist runner. `field` is the canonical field kind the
  // backend understands (title, description, clause_ref, assessor_notes,
  // field_description). `ownerKey` scopes the busy/undo state per-field so
  // multiple toolbars on the same panel don't clobber each other. `apply`
  // writes the new value back into state + persists. The previous value is
  // captured into `aiUndoMap[ownerKey]` so the user can one-click revert.
  const runAIAssist = async (opts: {
    action: 'generate' | 'expand' | 'improve' | 'rewrite' | 'summarise';
    field: 'title' | 'description' | 'clause_ref' | 'assessor_notes' | 'field_description';
    ownerKey: string;
    currentValue: string;
    context: {
      nodeType?: string;
      parentName?: string;
      frameworkName?: string;
      clauseRef?: string;
      sourceText?: string;
    };
    apply: (value: string) => Promise<void> | void;
  }) => {
    setAiBusyKey(opts.ownerKey);
    try {
      const res = await fetch('/api/compliance/definition/nodes/ai-assist', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({
          action: opts.action,
          field: opts.field,
          currentValue: opts.currentValue,
          ...opts.context,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'AI assist failed');
      }
      const data = await res.json();
      if (!data.success || !data.value) {
        notify.error(data.message || 'AI returned no content');
        return;
      }
      // Save undo snapshot BEFORE applying
      setAiUndoMap(prev => ({ ...prev, [opts.ownerKey]: opts.currentValue }));
      await opts.apply(data.value);
      notify.success('Updated by AI — undo available');
    } catch (e: any) {
      notify.error(e?.message || 'AI assist failed');
    } finally {
      setAiBusyKey(null);
    }
  };

  // Structured mode renderer — mirrors the AIFrameworkPreview list style
  // (type badge + title + inline edit + children nested with a left border),
  // but operates on the live template's `nodes` tree and persists via
  // `updateNode`. Works alongside the drag-drop visual mode; both read
  // and write the same state.
  const startStructEdit = (node: Node) => {
    setStructEditingId(node.id);
    setStructEditForm({ name: node.name || '', description: node.description || '' });
  };
  const cancelStructEdit = () => { setStructEditingId(null); };
  const saveStructEdit = async (node: Node) => {
    const updated: Node = { ...node, name: structEditForm.name, description: structEditForm.description };
    await updateNode(updated);
    setStructEditingId(null);
  };

  const renderStructNode = (node: Node, depth: number = 0): React.ReactElement => {
    const isEditing = structEditingId === node.id;
    // Colour the type badge per hierarchy level so scans read fast
    const typeColour =
      node.type === 'scope' ? 'bg-purple-100 text-purple-700' :
      node.type === 'section' ? 'bg-blue-100 text-blue-700' :
      node.type === 'sub_requirement' ? 'bg-emerald-100 text-emerald-700' :
      'bg-indigo-100 text-indigo-700';

    return (
      <div
        key={node.id}
        className={`border rounded-lg p-3 mb-2 bg-white ${selectedNode?.id === node.id ? 'border-indigo-400 ring-2 ring-indigo-100' : 'border-slate-200 hover:border-slate-300'} transition-all`}
        style={{ marginLeft: depth === 0 ? 0 : undefined }}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${typeColour}`}>
            {node.type}
          </span>
          {isEditing ? (
            <input
              type="text"
              value={structEditForm.name}
              onChange={(e) => setStructEditForm({ ...structEditForm, name: e.target.value })}
              className="flex-1 min-w-[200px] px-2 py-1 border border-slate-300 rounded text-sm font-semibold focus:ring-2 ring-indigo-100 focus:border-indigo-400 outline-none"
              placeholder="Title"
              autoFocus
            />
          ) : (
            <strong
              className="text-sm font-semibold text-slate-800 cursor-pointer hover:text-indigo-700"
              onClick={() => selectNode(node)}
              title="Click to focus this node in the properties panel"
            >
              {node.name || <span className="text-slate-400 italic">(untitled)</span>}
            </strong>
          )}
          {node.clause_ref && (
            <span className="text-[10px] font-mono text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
              {node.clause_ref}
            </span>
          )}
          {node.source_ref && node.source_ref !== node.clause_ref && (
            <span className="text-[10px] font-mono text-sky-700 bg-sky-50 px-1.5 py-0.5 rounded">
              {node.source_ref}
            </span>
          )}
          {!!node.generates_compliance_page && (
            <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded uppercase">page</span>
          )}
          <div className="ml-auto flex gap-1">
            {isEditing ? (
              <>
                <button
                  type="button"
                  onClick={() => saveStructEdit(node)}
                  className="px-2 py-1 text-[10px] font-bold uppercase rounded border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={cancelStructEdit}
                  className="px-2 py-1 text-[10px] font-bold uppercase rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => startStructEdit(node)}
                  className="px-2 py-1 text-[10px] font-bold uppercase rounded border border-slate-200 text-slate-700 hover:bg-slate-50"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => { selectNode(node); }}
                  className="px-2 py-1 text-[10px] font-bold uppercase rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
                  title="Open in properties panel for full editing (fields, compliance-page, clause_ref, etc.)"
                >
                  Details
                </button>
                {selectedTemplate?.status === 'draft' && (
                  <button
                    type="button"
                    onClick={() => deleteNode(node.id)}
                    className="px-2 py-1 text-[10px] font-bold uppercase rounded border border-red-200 text-red-600 hover:bg-red-50"
                  >
                    Delete
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        {isEditing ? (
          <textarea
            value={structEditForm.description}
            onChange={(e) => setStructEditForm({ ...structEditForm, description: e.target.value })}
            className="w-full mt-2 px-2 py-1 border border-slate-300 rounded text-xs focus:ring-2 ring-indigo-100 focus:border-indigo-400 outline-none"
            placeholder="Description"
            rows={3}
          />
        ) : (
          node.description && (
            <p className="text-xs text-slate-600 mt-1.5 leading-relaxed whitespace-pre-wrap">{node.description}</p>
          )
        )}
        {node.children && node.children.length > 0 && (
          <div className="ml-4 mt-2 border-l-2 border-slate-100 pl-3">
            {node.children.map(child => renderStructNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  // Thin per-field wrappers so the JSX call sites stay tidy.
  const nodeTitleBar = (node: Node) => renderAIAssistBar({
    field: 'title',
    ownerKey: `node:${node.id}:title`,
    currentValue: node.name || '',
    context: buildNodeContext(node),
    apply: async (v: string) => {
      const updated: Node = { ...node, name: v };
      setSelectedNode(updated);
      await updateNode(updated, { silent: true });
    },
  });
  const nodeDescriptionBar = (node: Node) => renderAIAssistBar({
    field: 'description',
    ownerKey: `node:${node.id}:description`,
    currentValue: node.description || '',
    context: buildNodeContext(node),
    apply: async (v: string) => {
      const updated: Node = { ...node, description: v };
      setSelectedNode(updated);
      await updateNode(updated, { silent: true });
    },
  });
  const nodeClauseRefBar = (node: Node) => renderAIAssistBar({
    field: 'clause_ref',
    ownerKey: `node:${node.id}:clause_ref`,
    currentValue: node.clause_ref || '',
    context: buildNodeContext(node),
    apply: async (v: string) => {
      const updated: Node = { ...node, clause_ref: v };
      setSelectedNode(updated);
      await updateNode(updated, { silent: true });
    },
  });
  const nodeAssessorNotesBar = (node: Node) => renderAIAssistBar({
    field: 'assessor_notes',
    ownerKey: `node:${node.id}:assessor_notes`,
    currentValue: node.assessor_only_notes || '',
    context: buildNodeContext(node),
    apply: async (v: string) => {
      const updated: Node = { ...node, assessor_only_notes: v };
      setSelectedNode(updated);
      await updateNode(updated, { silent: true });
    },
  });

  // Inline questions panel for the right-side properties pane.
  // Mounted under each requirement / sub_requirement node block.
  // Lazily fetches questions on selected-node change, lets the author add
  // guidance + evidence prompts, and persists them via the new
  // /api/compliance/definition/template-node-questions endpoints.
  const renderNodeQuestionsPanel = (node: Node) => {
    const realNodeId = (() => {
      const mapped = mockToReal.current.get(node.id);
      if (mapped) return mapped;
      const parsed = parseInt(node.id, 10);
      return isNaN(parsed) ? null : parsed;
    })();
    if (!realNodeId) {
      return (
        <div className="text-[10px] italic text-slate-400 mt-2">
          Save the node first before authoring questions.
        </div>
      );
    }
    return <NodeQuestionsPanel nodeId={realNodeId} />;
  };

  // Helper: build a clause-context object from the currently selected node.
  const buildNodeContext = (node: Node | null) => {
    if (!node) return {};
    let parentName: string | undefined;
    const findParent = (arr: Node[], targetId: string): Node | null => {
      for (const n of arr) {
        if (n.children?.some(c => c.id === targetId)) return n;
        if (n.children) {
          const found = findParent(n.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };
    const parent = findParent(nodes, node.id);
    if (parent) parentName = parent.name;
    return {
      nodeType: node.type,
      parentName,
      frameworkName: selectedTemplate?.name,
      clauseRef: node.clause_ref || undefined,
      sourceText: `${node.name || ''}\n${node.description || ''}`.trim(),
    };
  };

  // Render a compact AI assist toolbar. The toolbar has 4 always-on actions
  // (Expand / Improve / Summarise / Rewrite) plus Generate when the field
  // is empty, plus an Undo pill when a pre-AI snapshot exists for this key.
  const renderAIAssistBar = (opts: {
    field: 'title' | 'description' | 'clause_ref' | 'assessor_notes' | 'field_description';
    ownerKey: string;
    currentValue: string;
    context: {
      nodeType?: string;
      parentName?: string;
      frameworkName?: string;
      clauseRef?: string;
      sourceText?: string;
    };
    apply: (value: string) => Promise<void> | void;
    // Compact layout mode for tight spaces (e.g. inline next to an input)
    compact?: boolean;
  }) => {
    const busy = aiBusyKey === opts.ownerKey;
    const hasValue = !!(opts.currentValue && opts.currentValue.trim());
    const undoValue = aiUndoMap[opts.ownerKey];
    const hasUndo = undoValue !== undefined;
    const btnBase = 'px-2 py-1 text-[10px] font-bold uppercase rounded-md border transition-all disabled:opacity-40 disabled:cursor-not-allowed';
    const run = (action: 'generate' | 'expand' | 'improve' | 'rewrite' | 'summarise') =>
      runAIAssist({
        action,
        field: opts.field,
        ownerKey: opts.ownerKey,
        currentValue: opts.currentValue,
        context: opts.context,
        apply: opts.apply,
      });
    const undo = async () => {
      if (undoValue === undefined) return;
      await opts.apply(undoValue);
      setAiUndoMap(prev => {
        const next = { ...prev };
        delete next[opts.ownerKey];
        return next;
      });
      notify.success('Reverted to previous value');
    };
    return (
      <div className={`mt-2 flex flex-wrap items-center ${opts.compact ? 'gap-1' : 'gap-1.5'}`}>
        <span className="text-[9px] font-bold text-slate-400 uppercase mr-1">AI</span>
        <NewBadge featureId="ai-assist-toolbar" title="NEW: AI can generate, improve, rewrite, or summarise this field" />
        {!hasValue && (
          <button
            type="button"
            disabled={busy}
            onClick={() => run('generate')}
            className={`${btnBase} border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100`}
            title="Generate a new value from the clause context"
          >
            {busy ? '…' : 'Generate'}
          </button>
        )}
        <button type="button" disabled={busy || !hasValue} onClick={() => run('expand')}
          className={`${btnBase} border-slate-200 text-slate-700 bg-white hover:bg-slate-50`}>Expand</button>
        <button type="button" disabled={busy || !hasValue} onClick={() => run('improve')}
          className={`${btnBase} border-slate-200 text-slate-700 bg-white hover:bg-slate-50`}>Improve</button>
        <button type="button" disabled={busy || !hasValue} onClick={() => run('summarise')}
          className={`${btnBase} border-slate-200 text-slate-700 bg-white hover:bg-slate-50`}>Summarise</button>
        <button type="button" disabled={busy || !hasValue} onClick={() => run('rewrite')}
          className={`${btnBase} border-slate-200 text-slate-700 bg-white hover:bg-slate-50`}>Rewrite</button>
        {hasUndo && (
          <button type="button" disabled={busy} onClick={undo}
            className={`${btnBase} border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 ml-1`}
            title="Revert to the value before the last AI update">
            ↶ Undo
          </button>
        )}
        {busy && <span className="text-[9px] text-indigo-500 ml-1">thinking…</span>}
      </div>
    );
  };

  // Update field
  const updateField = async (field: TemplateField, opts: { silent?: boolean } = {}) => {
    try {
      const response = await fetch(`/api/compliance/definition/templates/fields/${field.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({
          // The builder UI shows a single "Field Name" input that users treat as
          // the human question. Compliance pages render `label || field_name`,
          // so keep both columns in sync — whatever the user typed becomes the
          // visible question on the compliance page.
          label: field.field_name,
          field_name: field.field_name,
          field_type: field.field_type,
          description: field.description,
          field_order: field.field_order,
          is_required: field.is_required,
          is_active: field.is_active,
          validation_rules: field.validation_rules,
          default_value: field.default_value,
          field_options: normalizeFieldOptions(field.field_options),
          is_evidence: field.is_evidence,
          allow_multiple: field.allow_multiple
        })
      });

      if (response.ok) {
        if (!opts.silent) notify.success('Field updated successfully');
        // Reload fields
        const nodeId = parseInt(field.node_id.toString());
        loadNodeFields(nodeId);
        // Trigger auto-save
        triggerAutoSave();
      } else {
        const error = await response.json();
        notify.error(error.error || 'Failed to update field');
      }
    } catch (error) {
      console.error('Error updating field:', error);
      notify.error('Failed to update field');
    }
  };

  // Reorder fields
  const reorderFields = async (dragged: TemplateField, target: TemplateField) => {
    const nodeId = parseInt(dragged.node_id.toString());
    const fields = nodeFields.get(nodeId.toString()) || [];
    const draggedIndex = fields.findIndex(f => f.id === dragged.id);
    const targetIndex = fields.findIndex(f => f.id === target.id);

    // Don't update if dropping on itself
    if (draggedIndex === targetIndex) return;

    const newFields = [...fields];
    newFields.splice(draggedIndex, 1);
    newFields.splice(targetIndex, 0, dragged);

    // Update field_order
    newFields.forEach((f, index) => f.field_order = index);

    // Update state
    setNodeFields(prev => {
      const newMap = new Map(prev);
      newMap.set(nodeId.toString(), newFields);
      return newMap;
    });

    // Update API for each field - make direct API calls to avoid multiple notifications
    try {
      for (const field of newFields) {
        const response = await fetch(`/api/compliance/definition/templates/fields/${field.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCsrfToken(),
          },
          body: JSON.stringify({
            field_name: field.field_name,
            field_type: field.field_type,
            description: field.description,
            field_order: field.field_order,
            is_required: field.is_required,
            is_active: field.is_active,
            validation_rules: field.validation_rules,
            default_value: field.default_value,
            field_options: normalizeFieldOptions(field.field_options),
            is_evidence: field.is_evidence,
            allow_multiple: field.allow_multiple
          })
        });

        if (!response.ok) {
          throw new Error('Failed to update field order');
        }
      }
      notify.success('Fields reordered successfully');
      // Trigger auto-save
      triggerAutoSave();
    } catch (error) {
      console.error('Error reordering fields:', error);
      notify.error('Failed to reorder fields');
    }
  };







  // Duplicate node
  const handleDuplicateNode = async (node: Node) => {
    if (!selectedTemplate) return;

    // Get the real node ID
    let realNodeId: number;
    const mappedId = mockToReal.current.get(node.id);
    if (mappedId) {
      realNodeId = mappedId;
    } else {
      const parsed = parseInt(node.id);
      if (isNaN(parsed)) {
        console.error('Invalid node ID for duplication:', node.id);
        notify.error('Invalid node ID');
        return;
      }
      realNodeId = parsed;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/compliance/definition/templates/nodes/${realNodeId}/duplicate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        }
      });

      if (response.ok) {
        notify.success('Node duplicated successfully');
        loadTemplateStructure(selectedTemplate.id);
        // Trigger auto-save
        triggerAutoSave();
      } else {
        const error = await response.json();
        notify.error(error.error || 'Failed to duplicate node');
      }
    } catch (error) {
      console.error('Error duplicating node:', error);
      notify.error('Failed to duplicate node');
    } finally {
      setLoading(false);
    }
  };

  // Duplicate field
  const handleDuplicateField = async (field: TemplateField, nodeId: string) => {
    // Get the real node ID first
    let realNodeId: number | undefined;
    const mappedId = mockToReal.current.get(nodeId);
    if (mappedId) {
      realNodeId = mappedId;
    } else {
      const parsed = parseInt(nodeId);
      if (!isNaN(parsed)) {
        realNodeId = parsed;
      }
    }

    if (!realNodeId) {
      console.error('Cannot duplicate field: node ID not found');
      notify.error('Invalid node ID');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/compliance/definition/templates/fields/${field.id}/duplicate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        }
      });

      if (response.ok) {
        notify.success('Field duplicated successfully');
        loadNodeFields(realNodeId);
        // Trigger auto-save
        triggerAutoSave();
      } else {
        const error = await response.json();
        notify.error(error.error || 'Failed to duplicate field');
      }
    } catch (error) {
      console.error('Error duplicating field:', error);
      notify.error('Failed to duplicate field');
    } finally {
      setLoading(false);
    }
  };

  // API functions
  const createTemplate = async (name: string, description: string) => {
    setLoading(true);
    try {
      const response = await fetch('/api/compliance/definition/templates', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({
          name,
          description,
          type: 'compliance',
          status: 'draft'
        })
      });

      if (response.ok) {
        const data = await response.json();
        notify.success('Template created successfully');
        return data.data;
      } else {
        const error = await response.json();
        notify.error(error.error || 'Failed to create template');
        return null;
      }
    } catch (error) {
      console.error('Error creating template:', error);
      notify.error('Failed to create template');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const createNode = async (templateId: number, name: string, nodeType: string, parentId?: number, nodeOrder?: number) => {
    // ─────────────────────────────────────────────────────────────────────
    // Phase 3 builder defaults (see docs/COMPLIANCE_LIFECYCLE_PLAN.md §6):
    // - requirement → page-generating by default, evidence_type='document'
    // - sub_requirement → NOT page-generating by default, because its parent
    //   requirement typically already is the page (one-page-per-branch rule).
    //   User opts in if they want finer-grained pages.
    // - scope / section → never page-generating (server enforces)
    //
    // Collapses ~8 clicks per requirement to ~2.
    // ─────────────────────────────────────────────────────────────────────
    const defaultGeneratesPage = nodeType === 'requirement';
    const defaultEvidenceRequired = nodeType === 'requirement';
    const defaultEvidenceType = nodeType === 'requirement' ? 'document' : null;

    try {
      const response = await fetch(`/api/compliance/definition/templates/${templateId}/nodes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({
          name,
          node_type: nodeType,
          parent_id: parentId || null,
          node_order: nodeOrder || 0,
          description: '',
          code: '',
          is_enabled: true,
          tags: {},
          applicability: {},
          generates_compliance_page: defaultGeneratesPage,
          evidence_required: defaultEvidenceRequired,
          evidence_type: defaultEvidenceType,
          assessor_only_notes: '',
          guidance: ''
        })
      });

      if (response.ok) {
        const data = await response.json();
        return data.data;
      } else {
        const error = await response.json();
        notify.error(error.error || 'Failed to create node');
        return null;
      }
    } catch (error) {
      console.error('Error creating node:', error);
      notify.error('Failed to create node');
      return null;
    }
  };

  const performDelete = async (nodeId: number | string) => {
    if (!selectedTemplate) return;

    // Convert nodeId to number if it's a string
    let realNodeId: number;
    if (typeof nodeId === 'string') {
      // Check if it's a mock ID (contains timestamp)
      if (nodeId.includes('-')) {
        // It's a mock ID, try to get the real ID from the map
        const mappedId = mockToReal.current.get(nodeId);
        if (mappedId) {
          realNodeId = mappedId;
        } else {
          // If not in map, try to parse as number
          const parsed = parseInt(nodeId);
          if (isNaN(parsed)) {
            console.error('Invalid node ID:', nodeId);
            notify.error('Invalid node ID');
            return;
          }
          realNodeId = parsed;
        }
      } else {
        // It's a string representation of a number
        const parsed = parseInt(nodeId);
        if (isNaN(parsed)) {
          console.error('Invalid node ID:', nodeId);
          notify.error('Invalid node ID');
          return;
        }
        realNodeId = parsed;
      }
    } else {
      realNodeId = nodeId;
    }

    try {
      const response = await fetch(`/api/compliance/definition/templates/nodes/${realNodeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'X-CSRF-Token': getCsrfToken() }
      });

      if (response.ok) {
        notify.success('Node and all child items deleted successfully');
        loadTemplateStructure(selectedTemplate.id);
        // Trigger auto-save
        triggerAutoSave();
      } else {
        const error = await response.json();
        notify.error(error.error || 'Failed to delete node');
      }
    } catch (error) {
      console.error('Error deleting node:', error);
      notify.error('Failed to delete node');
    }
  };

  const deleteNode = async (nodeId: number | string) => {
    // Show confirmation dialog for cascading delete
    const nodeToDelete = findNodeById(nodes, nodeId.toString());
    if (nodeToDelete) {
      const childCount = countChildNodes(nodeToDelete);
      const fieldCount = countChildFields(nodeToDelete);

      if (childCount > 0 || fieldCount > 0) {
        const confirmMessage = `Are you sure you want to delete this ${nodeToDelete.type}? This will also delete ${childCount} child ${childCount === 1 ? 'item' : 'items'} and ${fieldCount} field${fieldCount === 1 ? '' : 's'}.`;

        // Show confirmation modal using the modal system
        openModal({
          component: ConfirmModal,
          props: {
            title: 'Confirm Deletion',
            message: confirmMessage,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            type: 'danger',
            onConfirm: async () => {
              await performDelete(nodeId);
            },
            onCancel: () => {
              // Modal closes automatically
            }
          },
          options: {
            size: 'large',
            zIndex: 10000,
            disableSidebarOffset: true
          }
        });
        return;
      }
    }

    // No confirmation needed, delete directly
    await performDelete(nodeId);
  };

  // Helper function to count child nodes recursively
  const countChildNodes = (node: Node): number => {
    let count = 0;
    if (node.children) {
      count += node.children.length;
      node.children.forEach(child => {
        count += countChildNodes(child);
      });
    }
    return count;
  };

  // Helper function to count child fields recursively
  const countChildFields = (node: Node): number => {
    let count = 0;
    if (node.fields) {
      count += node.fields.length;
    }
    if (node.children) {
      node.children.forEach(child => {
        count += countChildFields(child);
      });
    }
    return count;
  };

  const handleCreateTemplate = async () => {
    if (!newTemplateName.trim()) return;

    setLoading(true);
    try {
      const response = await fetch('/api/compliance/definition/templates', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({
          name: newTemplateName,
          description: newTemplateDescription,
          type: 'compliance',
          status: 'draft'
        })
      });

      if (response.ok) {
        const data = await response.json();
        const templateId = data?.data?.id;

        setShowCreateTemplateForm(false);
        setNewTemplateName('');
        setNewTemplateDescription('');
        notify.success('Template created successfully');

        // Fetch full template details so status is present (needed to unlock builder components)
        let fullTemplate: Template | null = null;
        if (templateId) {
          try {
            const detailRes = await fetch(`/api/compliance/definition/templates/${templateId}`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (detailRes.ok) {
              const detailJson = await detailRes.json();
              fullTemplate = detailJson.data;
            }
          } catch (err) {
            console.error('Error loading created template details:', err);
          }
        }

        // Fallback: ensure at least draft status so builder enables drag sources immediately
        if (!fullTemplate && templateId) {
          fullTemplate = {
            id: templateId,
            name: newTemplateName,
            description: newTemplateDescription,
            status: 'draft',
            version: 1,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          } as Template;
        }

        if (fullTemplate) {
          setSelectedTemplate(fullTemplate);
          loadTemplateStructure(fullTemplate.id);

          // ───────────────────────────────────────────────────────────────
          // Phase 3 / migration 041: bind this draft template to the framework
          // version in the URL so re-opening the builder resumes on it instead
          // of offering to create a new template. See docs/COMPLIANCE_LIFECYCLE_PLAN.md §7 Phase 3.
          // ───────────────────────────────────────────────────────────────
          if (ctxVersionId && fullTemplate.id) {
            try {
              await fetch(`/api/compliance/definition/framework-versions/versions/${ctxVersionId}/bind-template`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`,
                  'Content-Type': 'application/json',
                  'X-CSRF-Token': getCsrfToken(),
                },
                body: JSON.stringify({ templateId: fullTemplate.id }),
              });
            } catch (bindErr) {
              // Non-blocking — user can still continue authoring, just won't auto-resume
              console.warn('Failed to bind template to version; resume-on-reopen may not work:', bindErr);
            }
          }
        }
      } else {
        const error = await response.json();
        notify.error(error.error || 'Failed to create template');
      }
    } catch (error) {
      console.error('Error creating template:', error);
      notify.error('Failed to create template');
    } finally {
      setLoading(false);
    }
  };

  const publishTemplate = async (templateId: number) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/compliance/definition/templates/${templateId}/publish`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        }
      });

      if (response.ok) {
        notify.success('Template published successfully');
        // Reload template to get updated status
        const templateResponse = await fetch(`/api/compliance/definition/templates/${templateId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (templateResponse.ok) {
          const data = await templateResponse.json();
          setSelectedTemplate(data.data);
        }
        return true;
      } else {
        const error = await response.json();
        notify.error(error.error || 'Failed to publish template');
        return false;
      }
    } catch (error) {
      console.error('Error publishing template:', error);
      notify.error('Failed to publish template');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const loadTemplateStructure = async (templateId: number) => {
    // Clear mock-to-real ID map when loading a new template
    mockToReal.current.clear();
    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/compliance/definition/templates/${templateId}/nodes?forBuilder=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        console.error('Failed to load template structure:', response.status);
        return;
      }

      const result = await response.json();
      const apiNodes: TemplateNode[] = result.data || [];

      if (apiNodes.length === 0) {
        setNodes([]);
        return;
      }

      // Build hierarchical structure using buildNodeTree
      const hierarchicalNodes = buildNodeTree(apiNodes);

      // Convert TemplateNode[] to Node[] for component state
      const convertToNode = (templateNode: TemplateNode): Node => {
        const result = {
          id: templateNode.id.toString(),
          type: templateNode.node_type as 'scope' | 'section' | 'requirement' | 'sub_requirement',
          name: templateNode.name,
          description: templateNode.description,
          children: templateNode.children?.map(convertToNode),
          generates_compliance_page: templateNode.generates_compliance_page,
          evidence_required: templateNode.evidence_required,
          evidence_type: templateNode.evidence_type,
          assessor_only_notes: templateNode.assessor_only_notes,
          clause_ref: (templateNode as any).clause_ref ?? null,
          source_ref: (templateNode as any).source_ref ?? null,
          node_order: (templateNode as any).order_index || 0
        } as Node;

        return result;
      };

      const convertedNodes = hierarchicalNodes.map(convertToNode);
      setNodes(convertedNodes);

      // Load fields for all requirement and sub_requirement nodes
      const loadAllFields = async () => {
        const fieldPromises: Promise<void>[] = [];

        const processNode = (node: Node) => {
          if (node.type === 'requirement' || node.type === 'sub_requirement') {
            const realNodeId = parseInt(node.id);
            fieldPromises.push(
              (async () => {
                try {
                  const response = await fetch(`/api/compliance/definition/templates/nodes/${realNodeId}/fields`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                  });
                  const data = await response.json();
                  const fields = (data.data || []).map((field: TemplateField) => ({
                    ...field,
                    field_options: normalizeFieldOptions(field.field_options)
                  }));

                  console.log(`Loaded fields for node ${node.id} (${realNodeId}):`, fields);

                  // Update nodeFields map
                  setNodeFields(prev => {
                    const newMap = new Map(prev);
                    newMap.set(node.id, fields);
                    return newMap;
                  });

                  // Update node with fields
                  setNodes(prev => {
                    const updateNodeFields = (nodes: Node[]): Node[] => {
                      return nodes.map(n => {
                        if (n.id === node.id) {
                          return { ...n, fields };
                        }
                        if (n.children) {
                          return { ...n, children: updateNodeFields(n.children) };
                        }
                        return n;
                      });
                    };
                    return updateNodeFields(prev);
                  });
                } catch (error) {
                  console.error('Error loading fields for node:', node.id, error);
                }
              })()
            );
          }
          if (node.children) {
            node.children.forEach(processNode);
          }
        };

        convertedNodes.forEach(processNode);
        await Promise.all(fieldPromises);
      };

      loadAllFields();
    } catch (error) {
      console.error('Error loading template structure:', error);
      notify.error('Failed to load template structure');
    }
  };

  // Auto-save template structure with debouncing
  const triggerAutoSave = useCallback(() => {
    if (!selectedTemplate || selectedTemplate.status !== 'draft') {
      return;
    }

    // Clear any pending auto-save
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Set unsaved changes flag
    setHasUnsavedChanges(true);

    // Schedule auto-save after 2 seconds of inactivity
    autoSaveTimeoutRef.current = setTimeout(async () => {
      try {
        setIsAutoSaving(true);
        const token = localStorage.getItem('token');

        // Prepare data for auto-save
        const autoSaveData = {
          nodes: nodes,
          nodeFields: Array.from(nodeFields.entries()).reduce((acc, [nodeId, fields]) => {
            acc[nodeId] = fields;
            return acc;
          }, {} as Record<string, TemplateField[]>),
          timestamp: new Date().toISOString()
        };

        const response = await fetch(`/api/compliance/definition/templates/${selectedTemplate.id}/auto-save`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCsrfToken(),
          },
          body: JSON.stringify(autoSaveData)
        });

        if (response.ok) {
          setLastAutoSaveTime(new Date());
          setHasUnsavedChanges(false);
          console.log('Template auto-saved successfully');
        } else {
          console.error('Auto-save failed:', response.status);
        }
      } catch (error) {
        console.error('Auto-save error:', error);
      } finally {
        setIsAutoSaving(false);
      }
    }, 2000); // 2 second debounce
  }, [selectedTemplate, nodes, nodeFields]);

  // Beforeunload event handler to warn about unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
        return ''; // Other browsers
      }
      return undefined;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Cleanup auto-save timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  // ───────────────────────────────────────────────────────────────────────
  // Phase 3: debounced auto-verify.
  //
  // Runs the combined Stage 1 + Stage 2 verification whenever the template
  // loads, nodes change, or a field is added/removed. The traffic-light in
  // the header header reflects the result in real time so users never have
  // to click Verify manually and never see "Publish & Activate" get accepted
  // only to silently fail server-side.
  // ───────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedTemplate) {
      setVerifyResult(null);
      return;
    }
    if (verifyDebounceRef.current) {
      clearTimeout(verifyDebounceRef.current);
    }
    verifyDebounceRef.current = setTimeout(async () => {
      setVerifyLoading(true);
      try {
        const qs = ctxPreviousVersionId ? `?previousVersionId=${ctxPreviousVersionId}` : '';
        const res = await fetch(
          `/api/compliance/definition/verify/templates/${selectedTemplate.id}/publish-readiness${qs}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'X-CSRF-Token': getCsrfToken(),
            },
          }
        );
        const json = await res.json();
        if (json?.success) {
          setVerifyResult(json.data);
        }
      } catch {
        // non-blocking — the traffic-light just stays stale until next edit
      } finally {
        setVerifyLoading(false);
      }
    }, 500);
    return () => {
      if (verifyDebounceRef.current) clearTimeout(verifyDebounceRef.current);
    };
  }, [selectedTemplate?.id, nodes, nodeFields, ctxPreviousVersionId]);

  // Handle ?name= URL parameter to pre-fill and open create form
  useEffect(() => {
    const nameParam = searchParams.get('name');
    if (nameParam && !selectedTemplate) {
      setNewTemplateName(nameParam);
      setShowCreateTemplateForm(true);
    }
  }, [searchParams]);

  // Handle templateId URL parameter for editing existing templates.
  // Gracefully handles missing templates (e.g. deep-links from
  // DocumentLibrary that still point at deleted templates) by clearing
  // the URL param and dropping the user on the template list, instead
  // of letting the page crash on .data access.
  useEffect(() => {
    const templateId = searchParams.get('templateId');
    if (!templateId) return;
    (async () => {
      try {
        const res = await fetch(`/api/compliance/definition/templates/${templateId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        });
        if (res.status === 404) {
          notify.error(`Template #${templateId} no longer exists. Loading the template list instead.`);
          // Clear the stale URL param so refresh doesn't loop back to 404.
          const next = new URLSearchParams(searchParams);
          next.delete('templateId');
          setSearchParams(next, { replace: true });
          return;
        }
        if (!res.ok) {
          notify.error(`Failed to load template (${res.status}). Loading the template list instead.`);
          return;
        }
        const data = await res.json();
        if (data?.data) {
          setSelectedTemplate(data.data);
          loadTemplateStructure(data.data.id);
        } else {
          notify.error('Template response was empty. Loading the template list instead.');
        }
      } catch (error) {
        console.error('Error loading template:', error);
        notify.error('Failed to load template — network error.');
      }
    })();
  }, [searchParams]);

  // Fetch recent templates for landing page
  useEffect(() => {
    if (selectedTemplate) return;
    fetch('/api/compliance/definition/templates', {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data?.data) {
          const getDate = (t: any) => t.created_at || t.createdAt || '';
          const sorted = [...data.data].sort((a: any, b: any) =>
            new Date(getDate(b)).getTime() - new Date(getDate(a)).getTime()
          );
          setRecentTemplates(sorted.slice(0, 3));
        }
      })
      .catch(() => {});
  }, [selectedTemplate]);

  // Select node
  const selectNode = (node: Node) => {
    setSelectedNode(node);
    setSelectedField(null); // Clear field selection
    setSelectedFieldOptionsText('');
    originalNodeRef.current = { ...node }; // Store original values
    // Load fields for requirement and sub_requirement nodes
    if (node.type === 'requirement' || node.type === 'sub_requirement') {
      let realNodeId: number | undefined;
      const mappedId = mockToReal.current.get(node.id);
      if (mappedId) {
        realNodeId = mappedId;
      } else {
        const parsed = parseInt(node.id);
        if (!isNaN(parsed)) {
          realNodeId = parsed;
        }
      }
      if (realNodeId) {
        loadNodeFields(realNodeId);
      }
    }
  };

  // Select field
  const selectField = (field: TemplateField) => {
    const normalizedField = {
      ...field,
      field_options: normalizeFieldOptions(field.field_options)
    };
    setSelectedField(normalizedField);
    setSelectedFieldOptionsText(normalizeFieldOptions(field.field_options).join('\n'));
    setSelectedNode(null); // Clear node selection
    originalFieldRef.current = { ...normalizedField }; // Store original values
  };

  // Check if field has changed
  const hasFieldChanged = (currentField: TemplateField) => {
    const original = originalFieldRef.current;
    if (!original) return false;
    const currentOptions = normalizeFieldOptions(currentField.field_options);
    const originalOptions = normalizeFieldOptions(original.field_options);
    const optionsChanged = JSON.stringify(currentOptions) !== JSON.stringify(originalOptions);

    return (
      currentField.field_name !== original.field_name ||
      currentField.field_type !== original.field_type ||
      currentField.description !== original.description ||
      currentField.is_required !== original.is_required ||
      currentField.is_evidence !== original.is_evidence ||
      currentField.allow_multiple !== original.allow_multiple ||
      optionsChanged
    );
  };

  // Check if node has changed
  const hasNodeChanged = (currentNode: Node) => {
    const original = originalNodeRef.current;
    if (!original) return false;

    // Basic properties that all nodes have
    let hasChanged = (
      currentNode.name !== original.name ||
      currentNode.description !== original.description
    );

    // Additional properties only for requirement and sub_requirement
    if (currentNode.type === 'requirement' || currentNode.type === 'sub_requirement') {
      hasChanged = hasChanged || (
        currentNode.generates_compliance_page !== original.generates_compliance_page ||
        currentNode.evidence_required !== original.evidence_required ||
        currentNode.evidence_type !== original.evidence_type ||
        currentNode.assessor_only_notes !== original.assessor_only_notes ||
        (currentNode.clause_ref ?? null) !== (original.clause_ref ?? null) ||
        (currentNode.source_ref ?? null) !== (original.source_ref ?? null)
      );
    }

    return hasChanged;
  };

  // Show empty state when no template is selected
  if (!selectedTemplate) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 py-6">
            {/* Hero Section */}
            <div className="mb-12 text-center">
              <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Vastabloc Structure Engine
              </div>
              <h2 className="text-4xl font-bold text-slate-800 mb-4 leading-tight">
                Build Compliance Frameworks<br/>
                <span className="text-blue-600">Without the Complexity</span>
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8 leading-relaxed">
                Design compliance templates with drag-and-drop. Define domains, sections, requirements, and evidence fields — then publish and map to frameworks.
              </p>
              
              {/* Quick Action Buttons */}
              <div className="flex flex-wrap items-center justify-center gap-4">
                <button
                  onClick={() => setShowCreateTemplateForm(true)}
                  className="group bg-indigo-600 text-white px-6 py-3 rounded-xl font-semibold text-sm transition-all hover:bg-indigo-700 flex items-center gap-2"
                >
                  <svg className="w-6 h-6 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path d="M12 4v16m8-8H4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Create New Template
                </button>
                <a
                  href="/compliance/definition/templates"
                  className="group bg-white text-slate-700 px-6 py-3 rounded-xl font-semibold text-sm hover:bg-slate-50 transition-all border border-slate-200 flex items-center gap-2"
                >
                  <svg className="w-6 h-6 text-slate-500 group-hover:text-indigo-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Browse Templates
                </a>
              </div>
            </div>

            {/* Create Template Form Modal */}
            {showCreateTemplateForm && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-lg w-full animate-scale-in">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path d="M12 4v16m8-8H4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-slate-800">Create New Template</h3>
                        <p className="text-sm text-slate-500">Start building your compliance framework</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setShowCreateTemplateForm(false);
                        setNewTemplateName('');
                        setNewTemplateDescription('');
                      }}
                      className="w-10 h-10 bg-slate-100 hover:bg-slate-200 rounded-xl flex items-center justify-center text-slate-600 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path d="M6 18L18 6M6 6l12 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                  <div className="space-y-5">
                    <div>
                      <label className="text-sm font-bold text-slate-700 mb-2 block">Template Name *</label>
                      <input
                        type="text"
                        value={newTemplateName}
                        onChange={(e) => setNewTemplateName(e.target.value)}
                        placeholder="e.g., ISO 27001:2022 Framework"
                        className="w-full p-4 border border-slate-200 rounded-xl focus:ring-2 ring-indigo-100 focus:border-indigo-400 outline-none transition-all text-base"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-bold text-slate-700 mb-2 block">Description</label>
                      <textarea
                        value={newTemplateDescription}
                        onChange={(e) => setNewTemplateDescription(e.target.value)}
                        placeholder="Describe what this compliance framework covers..."
                        rows={4}
                        className="w-full p-4 border border-slate-200 rounded-xl focus:ring-2 ring-indigo-100 focus:border-indigo-400 outline-none transition-all resize-none text-base"
                      />
                    </div>
                    <div className="flex gap-3 pt-2">
                      <button
                        onClick={handleCreateTemplate}
                        disabled={loading || !newTemplateName.trim()}
                        className="flex-1 bg-indigo-600 text-white py-4 rounded-xl font-bold text-base transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {loading ? (
                          <>
                            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Creating...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path d="M5 13l4 4L19 7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Create Template
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setShowCreateTemplateForm(false);
                          setNewTemplateName('');
                          setNewTemplateDescription('');
                        }}
                        className="px-6 py-4 bg-slate-100 text-slate-700 rounded-xl font-bold text-base hover:bg-slate-200 transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>

                  {/* Hierarchy guidance */}
                  <div className="mt-5 bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <p className="text-sm font-bold text-blue-900 mb-2">Template Structure Guide</p>
                    <p className="text-xs text-blue-800 mb-2">
                      Templates must follow this hierarchy:
                    </p>
                    <div className="text-xs text-blue-700 space-y-1 ml-2">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-indigo-400 flex-shrink-0" />
                        <span><strong>Scope</strong> — top-level grouping (e.g., "Information Security")</span>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <span className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />
                        <span><strong>Section</strong> — categories within a scope (e.g., "Access Management")</span>
                      </div>
                      <div className="flex items-center gap-2 ml-8">
                        <span className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
                        <span><strong>Requirement</strong> — the actual compliance control that generates a page</span>
                      </div>
                    </div>
                    <p className="text-[10px] text-blue-600 mt-2">
                      Requirements that generate compliance pages must have at least one evidence field defined.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Recently Created Templates */}
            {recentTemplates.length > 0 && (
              <div className="mb-16">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-slate-800">Recently Created Templates</h3>
                    <p className="text-slate-500 text-sm mt-1">Pick up where you left off</p>
                  </div>
                  <a
                    href="/compliance/definition/templates"
                    className="text-indigo-600 hover:text-indigo-700 font-semibold text-sm flex items-center gap-1 hover:underline"
                  >
                    View all
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path d="M9 5l7 7-7 7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {recentTemplates.map(template => (
                    <button
                      key={template.id}
                      onClick={() => {
                        setSelectedTemplate(template);
                        loadTemplateStructure(template.id);
                        // Persist selection in the URL so a page reload
                        // returns to this template instead of the landing list.
                        const next = new URLSearchParams(searchParams);
                        next.set('templateId', String(template.id));
                        setSearchParams(next, { replace: true });
                      }}
                      className="group text-left bg-white rounded-xl p-6 border border-slate-200 hover:border-indigo-300 transition-all hover:shadow-lg hover:shadow-indigo-100/50"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                          template.status === 'published'
                            ? 'bg-emerald-100 text-emerald-700'
                            : template.status === 'archived'
                            ? 'bg-slate-100 text-slate-500'
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {template.status.charAt(0).toUpperCase() + template.status.slice(1)}
                        </span>
                      </div>
                      <h4 className="font-bold text-slate-800 mb-1 truncate group-hover:text-indigo-700 transition-colors">{template.name}</h4>
                      {template.description && (
                        <p className="text-slate-500 text-sm leading-relaxed line-clamp-2 mb-3">{template.description}</p>
                      )}
                      <div className="flex items-center gap-2 text-xs text-slate-400 mt-auto">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {new Date((template as any).created_at || template.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* How It Works Section */}
            <div className="bg-slate-900 rounded-2xl p-8 mb-12">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-xl font-bold text-white mb-1">How It Works</h3>
                  <p className="text-slate-400 text-sm">Build your compliance structure in 4 steps</p>
                </div>
                <span className="text-[10px] font-bold text-indigo-400">Powered by Vastabloc</span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {/* Step 1 */}
                <div className="text-left">
                  <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center mb-4">
                    <span className="text-2xl font-bold text-white">1</span>
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2">Create Template</h4>
                  <p className="text-slate-400 text-sm">Start by creating a new compliance template with a descriptive name.</p>
                </div>

                {/* Step 2 */}
                <div className="text-left">
                  <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mb-4">
                    <span className="text-2xl font-bold text-white">2</span>
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2">Add Domains</h4>
                  <p className="text-slate-400 text-sm">Drag domain components to create major compliance areas.</p>
                </div>

                {/* Step 3 */}
                <div className="text-left">
                  <div className="w-16 h-16 bg-emerald-600 rounded-2xl flex items-center justify-center mb-4">
                    <span className="text-2xl font-bold text-white">3</span>
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2">Build Structure</h4>
                  <p className="text-slate-400 text-sm">Add sections, requirements, and sub-requirements as needed.</p>
                </div>

                {/* Step 4 */}
                <div className="text-left">
                  <div className="w-16 h-16 bg-purple-600 rounded-2xl flex items-center justify-center mb-4">
                    <span className="text-2xl font-bold text-white">4</span>
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2">Define Fields</h4>
                  <p className="text-slate-400 text-sm">Add evidence fields to collect compliance data from companies.</p>
                </div>
              </div>
            </div>

            {/* Component Types Preview */}
            <div className="mb-16">
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-slate-800 mb-3">Available Components</h3>
                <p className="text-slate-600 text-lg">Drag these components to build your compliance structure</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Domain */}
                <div className="bg-white rounded-2xl p-5 border-2 border-slate-900 hover:shadow-lg transition-all cursor-grab">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-sm">D</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800">Domain / Scope</h4>
                      <p className="text-xs text-slate-500 uppercase font-bold">Top Level</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">Major compliance areas like Information Security or Data Privacy</p>
                </div>

                {/* Section */}
                <div className="bg-white rounded-2xl p-5 border-2 border-slate-400 hover:border-slate-600 hover:shadow-lg transition-all cursor-grab">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-slate-400 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-sm">S</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800">Section</h4>
                      <p className="text-xs text-slate-500 uppercase font-bold">Second Level</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">Subdivisions within domains for organized requirements</p>
                </div>

                {/* Requirement */}
                <div className="bg-white rounded-2xl p-5 border border-slate-200 hover:border-emerald-500 hover:shadow-lg transition-all cursor-grab">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-sm">R</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800">Requirement</h4>
                      <p className="text-xs text-slate-500 uppercase font-bold">Third Level</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">Specific compliance requirements companies must meet</p>
                </div>

                {/* Sub-Requirement */}
                <div className="bg-white rounded-2xl p-5 border border-dashed border-slate-300 hover:border-amber-500 hover:shadow-lg transition-all cursor-grab">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-sm">SR</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800">Sub-Requirement</h4>
                      <p className="text-xs text-slate-500 uppercase font-bold">Fourth Level</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">Detailed sub-requirements for granular compliance</p>
                </div>
              </div>

              {/* Field Types */}
              <div className="mt-6 bg-white rounded-xl p-6 border-2 border-indigo-100">
                <h4 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path d="M4 6h16M4 12h16M4 18h7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Evidence Field Types
                </h4>
                <div className="flex flex-wrap gap-3">
                  {['Text', 'Number', 'Date', 'Boolean', 'Document', 'Calculated', 'Select', 'Multi Select', 'Textarea'].map((type) => (
                    <span key={type} className="px-4 py-2 bg-indigo-50 text-indigo-700 rounded-xl text-sm font-semibold border border-indigo-200">
                      {type}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Quick Links Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <a
                href="/compliance/definition/templates"
                className="group bg-indigo-50 rounded-xl p-6 border-2 border-indigo-100 hover:border-indigo-300 transition-all hover:shadow-lg"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-slate-800 mb-1">View All Templates</h4>
                    <p className="text-slate-600 text-sm">Browse and manage your existing compliance templates</p>
                  </div>
                </div>
              </a>

              <button
                onClick={() => setShowHelpPanel(true)}
                className="group bg-emerald-50 rounded-xl p-6 border-2 border-emerald-100 hover:border-emerald-300 transition-all hover:shadow-lg text-left"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-slate-800 mb-1">Read Documentation</h4>
                    <p className="text-slate-600 text-sm">Learn more about building compliance frameworks</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
      </div>
    );
  }

  // Builder View
  return (
    <div className="h-screen flex flex-col bg-white">
      {/* Header */}
      <header className="h-16 border-b border-slate-200/50 px-8 flex items-center justify-between shrink-0 bg-white z-10 shadow-sm">
        <div className="flex items-center gap-4">
          <button
            onClick={() => {
              setSelectedTemplate(null);
              setNodes([]);
              setSelectedNode(null);
              setNodeFields(new Map());
              // Clear templateId from URL so the landing list renders on reload
              const next = new URLSearchParams(searchParams);
              next.delete('templateId');
              setSearchParams(next, { replace: true });
            }}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400"
            title="Back to Templates"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M19 9l-7 7-7-7" strokeWidth="2.5"/>
            </svg>
          </button>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={selectedTemplate.name}
              onChange={(e) => setSelectedTemplate({ ...selectedTemplate, name: e.target.value })}
              onFocus={(e) => {
                (e.target as HTMLInputElement).dataset.originalName = selectedTemplate.name || '';
              }}
              onBlur={async (e) => {
                const trimmed = (selectedTemplate.name || '').trim();
                const original = (e.target as HTMLInputElement).dataset.originalName ?? '';
                if (trimmed === original.trim()) return;
                if (!trimmed) {
                  setSelectedTemplate({ ...selectedTemplate, name: original });
                  notify.error('Template name cannot be empty');
                  return;
                }
                try {
                  const res = await fetch(`/api/compliance/definition/templates/${selectedTemplate.id}`, {
                    method: 'PUT',
                    headers: {
                      'Authorization': `Bearer ${localStorage.getItem('token')}`,
                      'Content-Type': 'application/json',
                      'X-CSRF-Token': getCsrfToken(),
                    },
                    body: JSON.stringify({ name: trimmed }),
                  });
                  if (!res.ok) {
                    const err = await res.json().catch(() => ({}));
                    notify.error(err.error || 'Failed to save template name');
                    return;
                  }
                  notify.success('Template name saved');
                } catch (e: any) {
                  notify.error(e?.message || 'Failed to save template name');
                }
              }}
              title="Click to edit the template name"
              className="text-sm font-bold text-slate-800 uppercase tracking-tighter bg-transparent border-b border-transparent hover:border-slate-200 focus:border-indigo-400 focus:bg-white outline-none px-1 min-w-[200px] max-w-[600px] w-auto"
              style={{ width: `${Math.max(20, (selectedTemplate.name || '').length + 2)}ch` }}
            />
            <span className="text-sm font-bold text-slate-400 uppercase tracking-tighter">/ Builder</span>
            <NewBadge featureId="editable-template-title" title="NEW: click the template name to rename it" />
            <PageInfoButton />
            {/* Mode toggle: Visual drag-drop tree vs Structured inline-edit list.
                Both modes operate on the same `nodes` state, so switching
                never loses work. */}
            <div className="ml-4 flex items-center bg-slate-100 rounded-lg p-0.5">
              <button
                type="button"
                onClick={() => setBuilderMode('visual')}
                className={`px-3 py-1 text-[10px] font-bold uppercase rounded-md transition-all ${builderMode === 'visual' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                title="Drag-and-drop tree builder"
              >
                Visual
              </button>
              <button
                type="button"
                onClick={() => setBuilderMode('structured')}
                className={`px-3 py-1 text-[10px] font-bold uppercase rounded-md transition-all ${builderMode === 'structured' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                title="Inline structured list editor (like AI Framework Preview)"
              >
                Structured
              </button>
            </div>
            <NewBadge featureId="builder-mode-toggle" title="NEW: switch between drag-drop and structured list editing" />
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
            selectedTemplate.status === 'draft' ? 'bg-indigo-100 text-indigo-800' :
            selectedTemplate.status === 'published' ? 'bg-emerald-100 text-emerald-800' :
            'bg-slate-100 text-slate-800'
          }`}>
            {selectedTemplate.status}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-[10px] font-bold text-indigo-400 tracking-widest">Vastabloc</span>
          <span className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest">● Live</span>
          {isAutoSaving && (
            <span className="text-[10px] font-bold text-amber-500 uppercase tracking-widest flex items-center gap-1">
              <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </span>
          )}
          {lastAutoSaveTime && !isAutoSaving && (
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              Saved {lastAutoSaveTime.toLocaleTimeString()}
            </span>
          )}
          {hasUnsavedChanges && !isAutoSaving && (
            <span className="text-[10px] font-bold text-amber-500 uppercase tracking-widest">
              ● Unsaved changes
            </span>
          )}
          {/* Phase 3 — Persistent traffic-light. Always visible, always current.
              Click to expand the detailed issue list. */}
          {selectedTemplate.status === 'draft' && (() => {
            const status: 'pass' | 'warn' | 'fail' | 'pending' = verifyLoading
              ? 'pending'
              : (verifyResult?.status ?? 'pending');
            const blockerCount = verifyResult?.blockers?.length ?? 0;
            const warningCount = verifyResult?.warnings?.length ?? 0;
            const bg =
              status === 'pass' ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
              : status === 'warn' ? 'bg-amber-100 text-amber-800 border-amber-300'
              : status === 'fail' ? 'bg-red-100 text-red-800 border-red-300'
              : 'bg-slate-100 text-slate-500 border-slate-200';
            const label =
              status === 'pending' ? '…verifying'
              : status === 'pass' ? 'Ready to publish'
              : status === 'warn' ? `${warningCount} warning${warningCount !== 1 ? 's' : ''}`
              : `${blockerCount} blocker${blockerCount !== 1 ? 's' : ''}`;
            return (
              <button
                onClick={() => setShowVerifyPanel((v) => !v)}
                className={`px-3 py-1.5 rounded-lg border text-[11px] font-bold uppercase tracking-wide ${bg} hover:shadow-sm transition-all`}
                title="Live verification status — click to see details"
              >
                ● {label}
              </button>
            );
          })()}

          {/* AI review badge — shows how many architect-defaulted nodes need expert review */}
          {selectedTemplate.status === 'draft' && (() => {
            const countNeedsReview = (list: Node[]): number => {
              let count = 0;
              for (const n of list) {
                if ((n as any).needs_review) count++;
                if (n.children) count += countNeedsReview(n.children);
              }
              return count;
            };
            const reviewCount = countNeedsReview(nodes);
            if (reviewCount === 0) return null;
            return (
              <button
                onClick={() => {
                  // Find the first node needing review and jump to it
                  const findFirst = (list: Node[]): Node | null => {
                    for (const n of list) {
                      if ((n as any).needs_review) return n;
                      if (n.children) {
                        const f = findFirst(n.children);
                        if (f) return f;
                      }
                    }
                    return null;
                  };
                  const target = findFirst(nodes);
                  if (target) {
                    selectNode(target);
                    setTimeout(() => {
                      const el = document.querySelector(`[data-node-id="${target.id}"]`);
                      if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        el.classList.add('ring-4', 'ring-amber-400', 'ring-offset-2');
                        setTimeout(() => el.classList.remove('ring-4', 'ring-amber-400', 'ring-offset-2'), 2000);
                      }
                    }, 120);
                  }
                }}
                className="px-3 py-1.5 rounded-lg border bg-amber-100 text-amber-800 border-amber-300 text-[11px] font-bold uppercase tracking-wide hover:shadow-sm transition-all"
                title={`${reviewCount} AI-defaulted node${reviewCount === 1 ? '' : 's'} need expert review. Click to jump to the first one.`}
              >
                ⚠ {reviewCount} needs review
              </button>
            );
          })()}

          {selectedTemplate.status === 'draft' && !ctxVersionId && (
            <button
              onClick={() => selectedTemplate && publishTemplate(selectedTemplate.id)}
              disabled={loading || (verifyResult && !verifyResult.canProceed)}
              className="bg-emerald-600 text-white px-6 py-2 rounded-xl text-xs font-bold hover:shadow-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-md"
              title={verifyResult && !verifyResult.canProceed ? `Cannot publish: ${verifyResult.blockers.length} blocker(s). Click the status badge.` : 'Publish template (standalone)'}
            >
              {loading ? 'Publishing...' : 'Publish Structure'}
            </button>
          )}

          {/* Phase 3 — Publish & Activate (only when launched from a framework version context) */}
          {selectedTemplate.status === 'draft' && ctxVersionId && (
            <button
              onClick={async () => {
                if (!selectedTemplate || !ctxVersionId) return;
                setPublishActivateLoading(true);
                try {
                  const { lifecycleAuthorApi } = await import('@/domains/compliance/lifecycle/VerificationPanel');
                  const result = await lifecycleAuthorApi.publishAndActivate(selectedTemplate.id, ctxVersionId);
                  if (!result.ok) {
                    notify.error(result.error ?? 'Publish & Activate failed');
                    if (result.verification) setVerifyResult(result.verification as any);
                    setShowVerifyPanel(true);
                    return;
                  }
                  notify.success(
                    `Published & activated. ${result.data?.mappingsCreated ?? 0} node${(result.data?.mappingsCreated ?? 0) === 1 ? '' : 's'} mapped to framework version. Ready to assign.`
                  );
                  if (result.data?.archivedPriorVersionId) {
                    notify.warning(`Prior active version (id ${result.data.archivedPriorVersionId}) archived.`);
                  }
                  // Phase 3: reflect server state in local React state so the button
                  // disappears, the header badge flips to "published", and the
                  // auto-verify hook stops running. Without this, the user only sees
                  // the change after a page reload (bad UX).
                  setSelectedTemplate((prev) => (prev ? { ...prev, status: 'published' as any } : prev));
                  setVerifyResult(null);
                  setShowVerifyPanel(false);
                } catch (e: any) {
                  notify.error(e?.message ?? 'Publish & Activate failed');
                } finally {
                  setPublishActivateLoading(false);
                }
              }}
              disabled={publishActivateLoading || (verifyResult && !verifyResult.canProceed)}
              className="bg-indigo-600 text-white px-6 py-2 rounded-xl text-xs font-bold hover:shadow-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-md"
              title={
                verifyResult && !verifyResult.canProceed
                  ? `Cannot publish: ${verifyResult.blockers.length} blocker(s). Click the status badge.`
                  : `Run all 3 verification stages, publish template, activate framework version ${ctxVersionId}`
              }
            >
              {publishActivateLoading ? 'Publishing & Activating…' : '🚀 Publish & Activate'}
            </button>
          )}

          {/* Phase 3 — After publish: inline "Assign to Company" button.
              Opens the existing FrameworkAssignmentWizard with framework+version preselected,
              so the user lands on the company-picker step. The wizard auto-skips step 1. */}
          {selectedTemplate.status === 'published' && ctxFrameworkId && ctxVersionId && (
            <button
              onClick={() => {
                openModal({
                  component: FrameworkAssignmentWizard,
                  props: {
                    preselectedFrameworkId: ctxFrameworkId,
                    preselectedVersionId: ctxVersionId,
                    onComplete: () => notify.success('Assignment created. Company can now start filling evidence.'),
                  },
                  options: { size: 'large' },
                });
              }}
              className="bg-emerald-600 text-white px-6 py-2 rounded-xl text-xs font-bold hover:shadow-lg transition-all shadow-md"
              title={`Assign framework version ${ctxVersionId} to a company`}
            >
              👥 Assign to Company
            </button>
          )}

          {/* Phase 3 — After publish: breadcrumb back to Frameworks so the user
              knows where to go if they want to clone a new version or see the list */}
          {selectedTemplate.status === 'published' && ctxFrameworkId && ctxVersionId && (
            <button
              onClick={() => window.location.href = '/compliance/definition/frameworks'}
              className="bg-white text-slate-700 border border-slate-200 px-4 py-2 rounded-xl text-xs font-bold hover:bg-slate-50 transition-all"
              title="Back to the Frameworks list"
            >
              ← Frameworks
            </button>
          )}
        </div>
      </header>

      {/* Phase 3 — Verification panel (floating, top-right) */}
      {showVerifyPanel && (
        <div className="fixed top-24 right-6 z-50">
          <VerificationPanel
            result={verifyResult}
            loading={verifyLoading}
            title={
              ctxVersionId
                ? `Builder context: framework ${ctxFrameworkId}, version ${ctxVersionId}`
                : 'Standalone template authoring'
            }
            onClose={() => setShowVerifyPanel(false)}
            onIssueClick={(issue) => {
              if (!issue.nodeId) return;
              const targetId = String(issue.nodeId);
              const findNode = (list: Node[]): Node | null => {
                for (const n of list) {
                  if (n.id === targetId) return n;
                  if (n.children) {
                    const found = findNode(n.children);
                    if (found) return found;
                  }
                }
                return null;
              };
              const found = findNode(nodes);
              if (found) {
                selectNode(found);
                setShowVerifyPanel(false);
                setTimeout(() => {
                  const nodeEl = document.querySelector(`[data-node-id="${targetId}"]`);
                  if (nodeEl) {
                    nodeEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    nodeEl.classList.add('ring-4', 'ring-indigo-400', 'ring-offset-2');
                    setTimeout(() => nodeEl.classList.remove('ring-4', 'ring-indigo-400', 'ring-offset-2'), 2000);
                  }
                }, 150);
              }
            }}
          />
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Components */}
        <aside className="w-72 border-r bg-slate-50 p-6 flex flex-col gap-4 overflow-y-auto custom-scrollbar">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Components</p>

          <div className="grid grid-cols-2 gap-3">
            <DraggableComponent
              type="scope"
              name="Domain"
              description="Scope"
              onDragStart={handleDragStart}
              isDraft={selectedTemplate?.status === 'draft'}
            />

            <DraggableComponent
              type="section"
              name="Section"
              description="Section"
              onDragStart={handleDragStart}
              isDraft={selectedTemplate?.status === 'draft'}
            />

            <DraggableComponent
              type="requirement"
              name="Requirement"
              description="Atomic Control"
              onDragStart={handleDragStart}
              isDraft={selectedTemplate?.status === 'draft'}
            />

            <DraggableComponent
              type="sub_requirement"
              name="Sub-Requirement"
              description=""
              onDragStart={handleDragStart}
              isDraft={selectedTemplate?.status === 'draft'}
            />
          </div>

          <div className="border-t border-slate-200 my-4 pt-4">
            <p className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest mb-2">Fields</p>

            <div className="grid grid-cols-2 gap-3">
              <DraggableFieldComponent
                fieldType="text"
                name="Text"
                description="Single line text"
                onDragStart={handleFieldDragStart}
                isDraft={selectedTemplate?.status === 'draft'}
              />

              <DraggableFieldComponent
                fieldType="number"
                name="Number"
                description="Numeric input"
                onDragStart={handleFieldDragStart}
                isDraft={selectedTemplate?.status === 'draft'}
              />

              <DraggableFieldComponent
                fieldType="date"
                name="Date"
                description="Date picker"
                onDragStart={handleFieldDragStart}
                isDraft={selectedTemplate?.status === 'draft'}
              />

              <DraggableFieldComponent
                fieldType="boolean"
                name="Yes/No"
                description="Boolean toggle"
                onDragStart={handleFieldDragStart}
                isDraft={selectedTemplate?.status === 'draft'}
              />

              <DraggableFieldComponent
                fieldType="document"
                name="Document"
                description="File upload"
                onDragStart={handleFieldDragStart}
                isDraft={selectedTemplate?.status === 'draft'}
              />

              <DraggableFieldComponent
                fieldType="calculated"
                name="Calculated"
                description="Auto-computed"
                onDragStart={handleFieldDragStart}
                isDraft={selectedTemplate?.status === 'draft'}
              />

              <DraggableFieldComponent
                fieldType="select"
                name="Select"
                description="Dropdown list"
                onDragStart={handleFieldDragStart}
                isDraft={selectedTemplate?.status === 'draft'}
              />

              <DraggableFieldComponent
                fieldType="multiselect"
                name="Multi Select"
                description="Choose multiple"
                onDragStart={handleFieldDragStart}
                isDraft={selectedTemplate?.status === 'draft'}
              />

              <DraggableFieldComponent
                fieldType="textarea"
                name="Text Area"
                description="Multi-line text"
                onDragStart={handleFieldDragStart}
                isDraft={selectedTemplate?.status === 'draft'}
              />
            </div>
          </div>
        </aside>

        {/* Canvas - Main Builder Area */}
        <main
          ref={canvasRef}
          className="flex-1 overflow-y-auto p-12 bg-slate-50 flex flex-col items-center custom-scrollbar"
          onDragOver={(e) => {
            e.preventDefault();
          }}
          onDrop={(e) => {
            e.preventDefault();
            if (draggedType === 'scope') {
              handleCanvasDrop(draggedType);
            }
            setDraggedType(null);
          }}
        >
          <div className="w-full max-w-3xl space-y-10">
            {builderMode === 'structured' ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
                  <div>
                    <h3 className="text-sm font-bold text-slate-800 uppercase tracking-widest">Structured Editor</h3>
                    <p className="text-[11px] text-slate-500 mt-0.5">Inline edit titles and descriptions — same data as the visual tree.</p>
                  </div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase">{nodes.length} top-level scope{nodes.length === 1 ? '' : 's'}</span>
                </div>
                {nodes.length === 0 ? (
                  <div className="text-center py-12 text-xs text-slate-400 italic">
                    No nodes yet. Switch to Visual mode and drag a Domain to start, or use Describe Standard from the landing page.
                  </div>
                ) : (
                  <div>{nodes.map(n => renderStructNode(n))}</div>
                )}
              </div>
            ) : (
            <ComplianceStructureBuilderRenderer
              nodes={nodes}
              templateStatus={selectedTemplate?.status || 'draft'}
              nodeFields={nodeFields}
              selectedNode={selectedNode}
              draggedType={draggedType}
              draggedFieldType={draggedFieldType}
              activeTooltip={null}
              draggedField={draggedField}
              dragOverFieldId={dragOverFieldId}
              dragOverPosition={dragOverPosition}
              setDraggedType={setDraggedType}
              setDraggedFieldType={setDraggedFieldType}
              setDraggedField={setDraggedField}
              setDragOverFieldId={setDragOverFieldId}
              setDragOverPosition={setDragOverPosition}
              selectNode={selectNode}
              deleteNode={deleteNode}
              handleZoneDrop={handleZoneDrop}
              handleFieldDrop={handleFieldDrop}
              reorderFields={reorderFields}
              selectField={selectField}
              handleDuplicateNode={handleDuplicateNode}
              handleDuplicateField={handleDuplicateField}
              deleteField={deleteField}
              mockToReal={mockToReal}
            />
            )}

            {builderMode === 'visual' && nodes.length > 0 && selectedTemplate?.status === 'draft' && (
              <div
                className="border-4 border-dashed border-slate-200/50 rounded-[3rem] flex flex-col items-center justify-center text-slate-300 py-12 hover:border-indigo-300/50 hover:bg-indigo-50/10 transition-all bg-slate-50/30"
                onDragOver={(e) => {
                  e.preventDefault();
                  if (draggedType === 'scope') {
                    e.currentTarget.classList.add('drag-over');
                  }
                }}
                onDragLeave={(e) => {
                  e.currentTarget.classList.remove('drag-over');
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.remove('drag-over');
                  if (draggedType === 'scope') {
                    handleCanvasDrop(draggedType);
                  }
                  setDraggedType(null);
                }}
              >
                <svg className="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M12 6v6m0 0v6m0-6h6m-6 0H6" strokeWidth="2"/>
                </svg>
                <p className="font-bold text-xs uppercase tracking-widest">+ Drop Another Domain</p>
              </div>
            )}
          </div>

          {nodes.length === 0 && selectedTemplate?.status === 'draft' && (
            <div
              className="w-full max-w-3xl py-20 border-4 border-dashed border-slate-200/50 rounded-[3rem] flex flex-col items-center justify-center text-slate-300 mt-10 hover:border-indigo-300/50 hover:bg-indigo-50/10 transition-all bg-slate-50/30"
              onDragOver={(e) => {
                e.preventDefault();
                if (draggedType === 'scope') {
                  e.currentTarget.classList.add('drag-over');
                }
              }}
              onDragLeave={(e) => {
                e.currentTarget.classList.remove('drag-over');
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.remove('drag-over');
                if (draggedType === 'scope') {
                  handleCanvasDrop(draggedType);
                }
                setDraggedType(null);
              }}
            >
              <svg className="w-12 h-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M12 6v6m0 0v6m0-6h6m-6 0H6" strokeWidth="2"/>
              </svg>
              <p className="font-bold text-xs uppercase tracking-[0.3em]">Drop Domain to Start</p>
            </div>
          )}
        </main>

        {/* Right Sidebar - Properties */}
        <aside data-panel="node-properties" className="w-80 border-l bg-white p-8 overflow-y-auto custom-scrollbar">
          <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-10">Properties</h4>
          {selectedField ? (
            <div className="space-y-6">
              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">Field Name</label>
                <input
                  type="text"
                  value={selectedField.field_name}
                  onChange={(e) => setSelectedField({ ...selectedField, field_name: e.target.value })}
                  onBlur={() => {
                    if (hasFieldChanged(selectedField)) {
                      updateField(selectedField);
                    }
                  }}
                  className="w-full p-3 bg-slate-50/50 rounded-xl border border-slate-200/30 text-xs font-bold focus:ring-2 ring-indigo-100 outline-none focus:border-indigo-300 transition-all"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">Field Type</label>
                <select
                  value={selectedField.field_type}
                  onChange={(e) => {
                    const newType = e.target.value as FieldType;
                    if (newType !== selectedField.field_type) {
                      // Only reset the Field Name if the user hasn't typed a
                      // real question yet — i.e. the current name still matches
                      // a default placeholder. Otherwise keep Phil's text.
                      const currentName = (selectedField.field_name || '').trim();
                      const looksLikeDefault =
                        !currentName ||
                        /^new\s+.+\s+question$/i.test(currentName) ||
                        /^(text|number|date|boolean|document|calculated|select|multiselect|textarea)_field$/i.test(currentName) ||
                        /^(text|number|date|boolean|document|calculated|select|multi\s*select|textarea)$/i.test(currentName);
                      const nextName = looksLikeDefault
                        ? `New ${newType === 'boolean' ? 'yes/no' : newType} question`
                        : currentName;
                      const updatedOptions = isSelectLikeFieldType(newType)
                        ? normalizeFieldOptions(selectedField.field_options)
                        : [];
                      const updated = {
                        ...selectedField,
                        field_type: newType,
                        field_name: nextName,
                        field_options: updatedOptions,
                        allow_multiple: newType === 'multiselect'
                      };
                      setSelectedField(updated);
                      setSelectedFieldOptionsText(updatedOptions.join('\n'));
                      updateField(updated);
                    }
                  }}
                  className="w-full p-3 bg-slate-50/50 rounded-xl border border-slate-200/30 text-xs focus:ring-2 ring-indigo-100 outline-none focus:border-indigo-300 transition-all"
                >
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                  <option value="boolean">Yes/No</option>
                  <option value="document">Document</option>
                  <option value="calculated">Calculated</option>
                  <option value="select">Select</option>
                  <option value="multiselect">Select (Multiple)</option>
                  <option value="textarea">Text Area</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">Description</label>
                <textarea
                  value={selectedField.description || ''}
                  onChange={(e) => setSelectedField({ ...selectedField, description: e.target.value })}
                  onBlur={() => {
                    if (hasFieldChanged(selectedField)) {
                      updateField(selectedField);
                    }
                  }}
                  className="w-full h-24 p-3 bg-slate-50/50 rounded-xl border border-slate-200/30 text-xs focus:ring-2 ring-indigo-100 outline-none focus:border-indigo-300 transition-all"
                />
                {(() => {
                  // Grounding context: the parent node, so the model writes a
                  // helper sentence that fits the clause the field lives under.
                  const parentNode = findNodeById(nodes, selectedField.node_id?.toString?.() || '');
                  return renderAIAssistBar({
                    field: 'field_description',
                    ownerKey: `field:${selectedField.id}:description`,
                    currentValue: selectedField.description || '',
                    context: {
                      nodeType: `${selectedField.field_type} field`,
                      parentName: parentNode?.name,
                      frameworkName: selectedTemplate?.name,
                      clauseRef: parentNode?.clause_ref || undefined,
                      sourceText: `Field label: ${selectedField.field_name}. Parent clause: ${parentNode?.name || ''}. Parent description: ${parentNode?.description || ''}`.trim(),
                    },
                    apply: async (v: string) => {
                      const updated = { ...selectedField, description: v };
                      setSelectedField(updated);
                      await updateField(updated, { silent: true });
                    },
                  });
                })()}
              </div>
              {isSelectLikeFieldType(selectedField.field_type) && (
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">Dropdown Options (one per line)</label>
                  <textarea
                    value={selectedFieldOptionsText}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedFieldOptionsText(value);
                      setSelectedField({
                        ...selectedField,
                        field_options: parseOptionsFromEditorText(value)
                      });
                    }}
                    onBlur={() => {
                      const updatedField = {
                        ...selectedField,
                        field_options: parseOptionsFromEditorText(selectedFieldOptionsText)
                      };
                      setSelectedField(updatedField);
                      updateField(updatedField);
                    }}
                    placeholder={'Option 1\nOption 2\nOption 3'}
                    className="w-full h-28 p-3 bg-slate-50/50 rounded-xl border border-slate-200/30 text-xs focus:ring-2 ring-indigo-100 outline-none focus:border-indigo-300 transition-all"
                  />
                </div>
              )}
              {selectedField.field_type === 'calculated' && (() => {
                // Parse existing spec from field_options (which is normally an array for select,
                // but we reuse it as a JSON object {source, period, format} for calculated fields)
                let spec: any = {};
                try {
                  const raw: any = selectedField.field_options;
                  if (typeof raw === 'string') spec = JSON.parse(raw);
                  else if (raw && !Array.isArray(raw)) spec = raw;
                } catch { spec = {}; }
                const currentSource = spec.source || 'emissions.total_co2e';
                const currentPeriod = spec.period || 'current_year';
                const currentFormat = spec.format || 'tCO2e';

                const commitSpec = (next: any) => {
                  const updated = {
                    ...selectedField,
                    field_options: next as any,
                  };
                  setSelectedField(updated);
                  updateField(updated);
                };

                return (
                  <div className="p-4 bg-emerald-50/50 rounded-xl border border-emerald-100/50 space-y-3">
                    <div className="text-[10px] font-bold text-emerald-700 uppercase">Live Data Source</div>
                    <div>
                      <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Source</label>
                      <select
                        value={currentSource}
                        onChange={(e) => commitSpec({ source: e.target.value, period: currentPeriod, format: currentFormat })}
                        className="w-full p-2.5 bg-white rounded-lg border border-slate-200 text-xs focus:ring-2 ring-emerald-100 outline-none"
                      >
                        <optgroup label="Emissions">
                          <option value="emissions.total_co2e">Total Emissions (tCO2e)</option>
                          <option value="emissions.scope1">Scope 1 Emissions (tCO2e)</option>
                          <option value="emissions.scope2">Scope 2 Emissions (tCO2e)</option>
                          <option value="emissions.scope3">Scope 3 Emissions (tCO2e)</option>
                          <option value="emissions.offsets">Retired Offset Credits (tCO2e)</option>
                          <option value="emissions.net_emissions">Net Emissions (tCO2e, after offsets)</option>
                          <option value="emissions.target_progress">Target Progress (achieved / total)</option>
                          <option value="emissions.factors_count">Available Emission Factors</option>
                        </optgroup>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Period</label>
                      <select
                        value={currentPeriod}
                        onChange={(e) => commitSpec({ source: currentSource, period: e.target.value, format: currentFormat })}
                        className="w-full p-2.5 bg-white rounded-lg border border-slate-200 text-xs focus:ring-2 ring-emerald-100 outline-none"
                      >
                        <option value="current_year">Current Year</option>
                        <option value="previous_year">Previous Year</option>
                        <option value="ytd">Year to Date</option>
                        <option value="all_time">All Time</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Display Unit</label>
                      <input
                        type="text"
                        value={currentFormat}
                        onChange={(e) => commitSpec({ source: currentSource, period: currentPeriod, format: e.target.value })}
                        className="w-full p-2.5 bg-white rounded-lg border border-slate-200 text-xs focus:ring-2 ring-emerald-100 outline-none"
                        placeholder="tCO2e"
                      />
                    </div>
                    <p className="text-[10px] text-slate-500 leading-relaxed">
                      This field pulls live data from the company's emissions module at display time.
                      The compliance user sees the current value automatically — no manual entry.
                    </p>
                  </div>
                );
              })()}
              {isSelectLikeFieldType(selectedField.field_type) && (
                <div className="flex items-center justify-between p-4 bg-violet-50/50 rounded-xl border border-violet-100/40">
                  <span className="text-[10px] font-bold text-violet-700 uppercase">Allow Multiple Selections</span>
                  <input
                    type="checkbox"
                    checked={selectedField.field_type === 'multiselect' || !!selectedField.allow_multiple}
                    onChange={(e) => {
                      const isMulti = e.target.checked;
                      const updated = {
                        ...selectedField,
                        field_type: isMulti ? 'multiselect' : 'select',
                        allow_multiple: isMulti
                      };
                      setSelectedField(updated);
                      updateField(updated);
                    }}
                    className="w-4 h-4"
                  />
                </div>
              )}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-indigo-100/30">
                <span className="text-[10px] font-bold text-indigo-600 uppercase">Required</span>
                <input
                  type="checkbox"
                  checked={selectedField.is_required}
                  onChange={(e) => {
                    if (e.target.checked !== selectedField.is_required) {
                      const updated = { ...selectedField, is_required: e.target.checked };
                      setSelectedField(updated);
                      updateField(updated);
                    }
                  }}
                  className="w-4 h-4"
                />
              </div>
              {(() => {
                const fieldNode = findNodeById(nodes, selectedField.node_id.toString());
                return fieldNode && fieldNode.generates_compliance_page ? (
                  <div className="flex items-center justify-between p-4 bg-emerald-50/50 rounded-xl border border-emerald-100/30">
                    <span className="text-[10px] font-bold text-emerald-600 uppercase">Evidence Field</span>
                    <input
                      type="checkbox"
                      checked={selectedField.is_evidence || false}
                      onChange={(e) => {
                        if (e.target.checked !== (selectedField.is_evidence || false)) {
                          const updated = { ...selectedField, is_evidence: e.target.checked };
                          setSelectedField(updated);
                          updateField(updated);
                        }
                      }}
                      className="w-4 h-4"
                    />
                  </div>
                ) : null;
              })()}
            </div>
          ) : selectedNode ? (
            selectedNode.type === 'scope' ? (
              <div className="space-y-6">
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">Domain Title</label>
                  <input
                    type="text"
                    value={selectedNode.name}
                    onChange={(e) => setSelectedNode({ ...selectedNode, name: e.target.value })}
                    onBlur={() => {
                      if (hasNodeChanged(selectedNode)) {
                        updateNode(selectedNode);
                      }
                    }}
                    className="w-full p-3 bg-slate-50/50 rounded-xl border border-slate-200/30 text-xs font-bold focus:ring-2 ring-indigo-100 outline-none focus:border-indigo-300 transition-all"
                  />
                  {nodeTitleBar(selectedNode)}
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">Objective Description</label>
                  <textarea
                    value={selectedNode.description || ''}
                    onChange={(e) => setSelectedNode({ ...selectedNode, description: e.target.value })}
                    onBlur={() => {
                      if (hasNodeChanged(selectedNode)) {
                        updateNode(selectedNode);
                      }
                    }}
                    className="w-full h-32 p-3 bg-slate-50/50 rounded-xl border border-slate-200/30 text-xs focus:ring-2 ring-indigo-100 outline-none focus:border-indigo-300 transition-all"
                  />
                  {nodeDescriptionBar(selectedNode)}
                </div>
              </div>
            ) : selectedNode.type === 'section' ? (
              <div className="space-y-6">
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">Section Title</label>
                  <input
                    type="text"
                    value={selectedNode.name}
                    onChange={(e) => setSelectedNode({ ...selectedNode, name: e.target.value })}
                    onBlur={() => {
                      if (hasNodeChanged(selectedNode)) {
                        updateNode(selectedNode);
                      }
                    }}
                    className="w-full p-3 bg-slate-50/50 rounded-xl border border-slate-200/30 text-xs font-bold focus:ring-2 ring-indigo-100 outline-none focus:border-indigo-300 transition-all"
                  />
                  {nodeTitleBar(selectedNode)}
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">Section Description</label>
                  <textarea
                    value={selectedNode.description || ''}
                    onChange={(e) => setSelectedNode({ ...selectedNode, description: e.target.value })}
                    onBlur={() => {
                      if (hasNodeChanged(selectedNode)) {
                        updateNode(selectedNode);
                      }
                    }}
                    className="w-full h-32 p-3 bg-slate-50/50 text-xs focus:ring-2 ring-indigo-100 outline-none focus:border-indigo-300 transition-all"
                  />
                  {nodeDescriptionBar(selectedNode)}
                </div>
              </div>
            ) : selectedNode.type === 'sub_requirement' ? (
              <div className="space-y-6">
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">Title</label>
                  <input
                    type="text"
                    value={selectedNode.name}
                    onChange={(e) => setSelectedNode({ ...selectedNode, name: e.target.value })}
                    onBlur={() => {
                      if (hasNodeChanged(selectedNode)) {
                        updateNode(selectedNode);
                      }
                    }}
                    className="w-full p-3 bg-slate-50/50 rounded-xl border border-slate-200/30 text-xs font-bold focus:ring-2 ring-indigo-100 outline-none focus:border-indigo-300 transition-all"
                  />
                  {nodeTitleBar(selectedNode)}
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">Description</label>
                  <textarea
                    value={selectedNode.description || ''}
                    onChange={(e) => setSelectedNode({ ...selectedNode, description: e.target.value })}
                    onBlur={() => {
                      if (hasNodeChanged(selectedNode)) {
                        updateNode(selectedNode);
                      }
                    }}
                    className="w-full h-24 p-3 bg-slate-50/50 rounded-xl border border-slate-200/30 text-xs focus:ring-2 ring-indigo-100 outline-none focus:border-indigo-300 transition-all"
                  />
                  {nodeDescriptionBar(selectedNode)}
                </div>
                {/* Phase 3: compliance-page controls only on requirement / sub_requirement.
                    evidence_required is implied by generates_compliance_page and hidden.
                    Always show even during conflicts so the user can untick to resolve. */}
                {(selectedNode.type === 'requirement' || selectedNode.type === 'sub_requirement') && (
                  <div className={`flex items-center justify-between p-4 rounded-xl border ${
                    hasConflictingCompliancePage(nodes, selectedNode)
                      ? 'bg-amber-50 border-amber-300'
                      : 'bg-emerald-50 border-emerald-200'
                  }`}>
                    <div>
                      <span className={`text-[10px] font-bold uppercase ${
                        hasConflictingCompliancePage(nodes, selectedNode) ? 'text-amber-700' : 'text-emerald-700'
                      }`}>Generates Compliance Page</span>
                      {hasConflictingCompliancePage(nodes, selectedNode) ? (
                        <p className="text-[10px] text-amber-600 mt-0.5">Conflict: another node in this branch also generates a page. Only one per branch is allowed — untick this one or the other. If you need both to generate pages, move one into a separate branch (e.g. create it as a sibling requirement instead of a child).</p>
                      ) : (
                        <p className="text-[10px] text-emerald-600 mt-0.5">Companies fill this page when assigned</p>
                      )}
                    </div>
                    <input
                      type="checkbox"
                      checked={selectedNode.generates_compliance_page || false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const updated: Node = {
                          ...selectedNode,
                          generates_compliance_page: checked,
                          evidence_required: checked ? true : false,
                          evidence_type: checked ? (selectedNode.evidence_type || 'document') : undefined,
                        };
                        setSelectedNode(updated);
                        updateNode(updated);
                      }}
                      className="w-5 h-5 accent-emerald-600"
                    />
                  </div>
                )}
                {(selectedNode.type === 'requirement' || selectedNode.type === 'sub_requirement') &&
                 selectedNode.generates_compliance_page && (
                  <>
                    <div>
                      <label className="text-[10px] font-bold text-indigo-700 uppercase mb-2 block">
                        Clause Reference *
                      </label>
                      <input
                        type="text"
                        value={selectedNode.clause_ref ?? ''}
                        placeholder="e.g. ISO27001:A.5.1 or SOC2:CC6.1"
                        onChange={(e) => setSelectedNode({ ...selectedNode, clause_ref: e.target.value })}
                        onBlur={() => {
                          if (hasNodeChanged(selectedNode)) {
                            updateNode(selectedNode);
                          }
                        }}
                        className="w-full p-3 bg-indigo-50/30 rounded-xl border border-indigo-200 text-xs font-mono focus:ring-2 ring-indigo-100 outline-none focus:border-indigo-400 transition-all"
                      />
                      <p className="text-[10px] text-slate-500 mt-1 italic">
                        Stable identifier that survives framework version upgrades. Evidence is anchored to this, not the node name.
                      </p>
                      {nodeClauseRefBar(selectedNode)}
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-sky-700 uppercase mb-2 block">
                        Source Reference
                      </label>
                      <input
                        type="text"
                        value={selectedNode.source_ref ?? ''}
                        placeholder="e.g. Box 3, Table 2 row 4, D.7.3"
                        onChange={(e) => setSelectedNode({ ...selectedNode, source_ref: e.target.value })}
                        onBlur={() => {
                          if (hasNodeChanged(selectedNode)) {
                            updateNode(selectedNode);
                          }
                        }}
                        className="w-full p-3 bg-sky-50/40 rounded-xl border border-sky-200 text-xs font-mono focus:ring-2 ring-sky-100 outline-none focus:border-sky-400 transition-all"
                      />
                      <p className="text-[10px] text-slate-500 mt-1 italic">
                        Exact source locator from the standard for auditor traceability when the requirement comes from a box, table row, annex item, or section label.
                      </p>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">Evidence Type</label>
                      <select
                        value={selectedNode.evidence_type || 'document'}
                        onChange={(e) => {
                          const updated = { ...selectedNode, evidence_type: e.target.value as 'document' | 'number' | 'text' | 'mixed' };
                          setSelectedNode(updated);
                          updateNode(updated);
                        }}
                        className="w-full p-3 bg-slate-50/50 rounded-xl border border-slate-200/30 text-xs focus:ring-2 ring-indigo-100 outline-none focus:border-indigo-300 transition-all"
                      >
                        <option value="document">Document (upload)</option>
                        <option value="text">Text answer</option>
                        <option value="number">Number value</option>
                        <option value="mixed">Mixed (multiple field types)</option>
                      </select>
                    </div>
                  </>
                )}
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">Assessor Only Notes</label>
                  <textarea
                    value={selectedNode.assessor_only_notes || ''}
                    onChange={(e) => setSelectedNode({ ...selectedNode, assessor_only_notes: e.target.value })}
                    onBlur={() => {
                      if (hasNodeChanged(selectedNode)) {
                        updateNode(selectedNode);
                      }
                    }}
                    className="w-full h-24 p-3 bg-slate-50/50 rounded-xl border border-slate-200/30 text-xs focus:ring-2 ring-indigo-100 outline-none focus:border-indigo-300 transition-all"
                  />
                  {nodeAssessorNotesBar(selectedNode)}
                </div>
                {(selectedNode.type === 'requirement' || selectedNode.type === 'sub_requirement')
                  && !!selectedNode.generates_compliance_page
                  && renderNodeQuestionsPanel(selectedNode)}
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">Title</label>
                  <input
                    type="text"
                    value={selectedNode.name}
                    onChange={(e) => setSelectedNode({ ...selectedNode, name: e.target.value })}
                    onBlur={() => {
                      if (hasNodeChanged(selectedNode)) {
                        updateNode(selectedNode);
                      }
                    }}
                    className="w-full p-3 bg-slate-50/50 rounded-xl border border-slate-200/30 text-xs font-bold focus:ring-2 ring-indigo-100 outline-none focus:border-indigo-300 transition-all"
                  />
                  {nodeTitleBar(selectedNode)}
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">Description</label>
                  <textarea
                    value={selectedNode.description || ''}
                    onChange={(e) => setSelectedNode({ ...selectedNode, description: e.target.value })}
                    onBlur={() => {
                      if (hasNodeChanged(selectedNode)) {
                        updateNode(selectedNode);
                      }
                    }}
                    className="w-full h-24 p-3 bg-slate-50/50 rounded-xl border border-slate-200/30 text-xs focus:ring-2 ring-indigo-100 outline-none focus:border-indigo-300 transition-all"
                  />
                  {nodeDescriptionBar(selectedNode)}
                </div>
                {/* Phase 3: compliance-page controls only on requirement / sub_requirement.
                    evidence_required is implied by generates_compliance_page and hidden.
                    Always show even during conflicts so the user can untick to resolve. */}
                {(selectedNode.type === 'requirement' || selectedNode.type === 'sub_requirement') && (
                  <div className={`flex items-center justify-between p-4 rounded-xl border ${
                    hasConflictingCompliancePage(nodes, selectedNode)
                      ? 'bg-amber-50 border-amber-300'
                      : 'bg-emerald-50 border-emerald-200'
                  }`}>
                    <div>
                      <span className={`text-[10px] font-bold uppercase ${
                        hasConflictingCompliancePage(nodes, selectedNode) ? 'text-amber-700' : 'text-emerald-700'
                      }`}>Generates Compliance Page</span>
                      {hasConflictingCompliancePage(nodes, selectedNode) ? (
                        <p className="text-[10px] text-amber-600 mt-0.5">Conflict: another node in this branch also generates a page. Only one per branch is allowed — untick this one or the other. If you need both to generate pages, move one into a separate branch (e.g. create it as a sibling requirement instead of a child).</p>
                      ) : (
                        <p className="text-[10px] text-emerald-600 mt-0.5">Companies fill this page when assigned</p>
                      )}
                    </div>
                    <input
                      type="checkbox"
                      checked={selectedNode.generates_compliance_page || false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const updated: Node = {
                          ...selectedNode,
                          generates_compliance_page: checked,
                          evidence_required: checked ? true : false,
                          evidence_type: checked ? (selectedNode.evidence_type || 'document') : undefined,
                        };
                        setSelectedNode(updated);
                        updateNode(updated);
                      }}
                      className="w-5 h-5 accent-emerald-600"
                    />
                  </div>
                )}
                {(selectedNode.type === 'requirement' || selectedNode.type === 'sub_requirement') &&
                 selectedNode.generates_compliance_page && (
                  <>
                    <div>
                      <label className="text-[10px] font-bold text-indigo-700 uppercase mb-2 block">
                        Clause Reference *
                      </label>
                      <input
                        type="text"
                        value={selectedNode.clause_ref ?? ''}
                        placeholder="e.g. ISO27001:A.5.1 or SOC2:CC6.1"
                        onChange={(e) => setSelectedNode({ ...selectedNode, clause_ref: e.target.value })}
                        onBlur={() => {
                          if (hasNodeChanged(selectedNode)) {
                            updateNode(selectedNode);
                          }
                        }}
                        className="w-full p-3 bg-indigo-50/30 rounded-xl border border-indigo-200 text-xs font-mono focus:ring-2 ring-indigo-100 outline-none focus:border-indigo-400 transition-all"
                      />
                      <p className="text-[10px] text-slate-500 mt-1 italic">
                        Stable identifier that survives framework version upgrades. Evidence is anchored to this, not the node name.
                      </p>
                      {nodeClauseRefBar(selectedNode)}
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-sky-700 uppercase mb-2 block">
                        Source Reference
                      </label>
                      <input
                        type="text"
                        value={selectedNode.source_ref ?? ''}
                        placeholder="e.g. Box 3, Table 2 row 4, D.7.3"
                        onChange={(e) => setSelectedNode({ ...selectedNode, source_ref: e.target.value })}
                        onBlur={() => {
                          if (hasNodeChanged(selectedNode)) {
                            updateNode(selectedNode);
                          }
                        }}
                        className="w-full p-3 bg-sky-50/40 rounded-xl border border-sky-200 text-xs font-mono focus:ring-2 ring-sky-100 outline-none focus:border-sky-400 transition-all"
                      />
                      <p className="text-[10px] text-slate-500 mt-1 italic">
                        Exact source locator from the standard for auditor traceability when the requirement comes from a box, table row, annex item, or section label.
                      </p>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">Evidence Type</label>
                      <select
                        value={selectedNode.evidence_type || 'document'}
                        onChange={(e) => {
                          const updated = { ...selectedNode, evidence_type: e.target.value as 'document' | 'number' | 'text' | 'mixed' };
                          setSelectedNode(updated);
                          updateNode(updated);
                        }}
                        className="w-full p-3 bg-slate-50/50 rounded-xl border border-slate-200/30 text-xs focus:ring-2 ring-indigo-100 outline-none focus:border-indigo-300 transition-all"
                      >
                        <option value="document">Document (upload)</option>
                        <option value="text">Text answer</option>
                        <option value="number">Number value</option>
                        <option value="mixed">Mixed (multiple field types)</option>
                      </select>
                    </div>
                  </>
                )}
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">Assessor Only Notes</label>
                  <textarea
                    value={selectedNode.assessor_only_notes || ''}
                    onChange={(e) => setSelectedNode({ ...selectedNode, assessor_only_notes: e.target.value })}
                    onBlur={() => {
                      if (hasNodeChanged(selectedNode)) {
                        updateNode(selectedNode);
                      }
                    }}
                    className="w-full h-24 p-3 bg-slate-50/50 rounded-xl border border-slate-200/30 text-xs focus:ring-2 ring-indigo-100 outline-none focus:border-indigo-300 transition-all"
                  />
                  {nodeAssessorNotesBar(selectedNode)}
                </div>
                {(selectedNode.type === 'requirement' || selectedNode.type === 'sub_requirement')
                  && !!selectedNode.generates_compliance_page
                  && renderNodeQuestionsPanel(selectedNode)}
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">Reference ID</label>
                  <input
                    type="text"
                    value={`REQ-${selectedNode.id.split('-')[1] || selectedNode.id}`}
                    className="w-full p-3 bg-slate-100/50 rounded-xl border border-slate-200/30 text-xs font-bold"
                    readOnly
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">Guidance Text</label>
                  <textarea
                    value={`Assessor guidance for ${selectedNode.name}`}
                    readOnly
                    className="w-full h-32 p-3 bg-slate-50/50 rounded-xl border border-slate-200/30 text-xs focus:ring-2 ring-indigo-100 outline-none focus:border-indigo-300 transition-all"
                  />
                </div>
              </div>
            )
          ) : (
            <div className="text-center mt-20">
              <p className="text-xs text-slate-300 italic px-10">Select a node or field to view properties</p>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default ComplianceStructureBuilder;
